// src/views/portal/PortalCondomino.jsx
import { abrirCamera as openCamera, capturarFoto as captureFrame, pararCamera as stopCamera } from "../../utils/camera";
import React, { useState, useEffect, useCallback, useRef } from "react";

import ReservasPortal from './ReservasPortal';
import { useAuth } from "../../contexts/AuthContext";
import portalService from "../../services/portalService";
import api from "../../services/api";
import portariaService from "../../services/portariaService";
import useSocket from "../../hooks/useSocket";
import { getApiBaseUrl } from "../../services/apiBase";
import Avatar from "../../components/ui/Avatar";
import AvatarPicker from "../../components/ui/AvatarPicker";
import ConfirmModal from "../../components/ui/ConfirmModal";
import Banner from "../../components/ui/Banner";
import PasswordStrengthBar from "../../components/ui/PasswordStrengthBar";
import PortalContatoForm from "./PortalContatoForm";
import PortalNotificacoesForm from "./PortalNotificacoesForm";
import PortalSessoesForm from "./PortalSessoesForm";
import PwaInstallHint from "../../components/ui/PwaInstallHint";
import ThemeToggle from "../../components/layout/ThemeToggle";
import {
  HistoricoAviso,
  AbasAvisos,
  AvisoCardColapsavel,
  filtrarAvisos,
  contarAvisos,
} from "../portaria/AvisosShared";

/* ─── helpers ───────────────────────────────────────────────────── */
const fmtD  = d => { try{ const s=(d||"").split("T")[0]; return s?new Date(s+"T12:00:00").toLocaleDateString("pt-BR"):"—"; }catch{return"—";} };
const fmtDT = d => { try{ return new Date(d).toLocaleString("pt-BR",{day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"}); }catch{return"—";} };
const fmtVal= v => parseFloat(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});

const STC = { Gerado:"#1D4ED8", Registrado:"#7C3AED", Pago:"#166534", Vencido:"#991B1B", Cancelado:"#64748B" };
const STB = { Gerado:"#EFF6FF", Registrado:"#F5F3FF", Pago:"#DCFCE7", Vencido:"#FEE2E2", Cancelado:"#F1F5F9" };

const TABS = [
  { id:"inicio",       icon:"bi-house-fill",              label:"Início"       },
  { id:"boletos",      icon:"bi-receipt",                 label:"Boletos"      },
  { id:"reservas",     icon:"bi-calendar-check",          label:"Reservas"     },
  { id:"assembleias",  icon:"bi-people",                  label:"Assembleias"  },
  { id:"comunicados",  icon:"bi-megaphone",               label:"Comunicados"  },
  { id:"demonstrativos",icon:"bi-file-earmark-bar-graph",  label:"Demonstrativos"},
  { id:"encomendas",   icon:"bi-box-seam",                label:"Encomendas"   },
  { id:"ocorrencias",  icon:"bi-exclamation-circle",      label:"Ocorrências"  },
  { id:"prestacao",    icon:"bi-file-earmark-bar-graph",  label:"Prestação"    },
  { id:"autorizacoes", icon:"bi-qr-code",                 label:"Autorizações" },
  { id:"moradores",    icon:"bi-people-fill",             label:"Moradores"    },
  { id:"historico",    icon:"bi-clock-history",           label:"Histórico"    },
  { id:"veiculos",     icon:"bi-car-front",               label:"Veículos"     },
  { id:"pets",         icon:"bi-heart",                   label:"Pets"         },
  { id:"portaria",     icon:"bi-shield-check",            label:"Portaria"     },
];

const E_OCO  = { idCondominio:"", idUnidade:"", titulo:"", descricao:"", tpOcorrencia:"Reclamacao", prioridade:"Media" };
const OCO_ANEXO_MAX = 3;
const OCO_ANEXO_MAX_BYTES = 2 * 1024 * 1024;
const OCO_ANEXO_ACCEPT = "image/jpeg,image/png,image/gif,image/webp,application/pdf";
const OCO_ANEXO_MIMES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf"]);
const OCO_PRIOR_HINT = {
  Urgente: "Situação crítica — avise a administração se for risco à segurança.",
  Alta:    "Precisa de retorno o quanto antes.",
  Media:   "Fluxo normal de atendimento.",
  Baixa:   "Sem urgência — pode aguardar a rotina.",
};
const E_AUTH = { nmVisitante:"", documento:"", tpVisita:"Visitante", dtValidade:"", hrInicio:"", hrFim:"", observacao:"", emailVisitante:"" };
const E_MOR  = { nmMorador:"", tpMorador:"Familiar", parentesco:"", cpf:"", telefone:"" };

export default function PortalCondomino({ onLogout }) {
  const { usuario, logout } = useAuth();
  const sair = onLogout || logout;
  const tabsRef = useRef(null);

  /* ── state ── */
  const [aba,          setAba]          = useState("inicio");
  const [unidades,     setUnidades]     = useState([]);
  const [condoAtivo,   setCondoAtivo]   = useState(null); // { idCondominio, idUnidade, nmFantasia, nmUnidade... }
  const [showCondoSel, setShowCondoSel] = useState(false);
  const [alertas,      setAlertas]      = useState({ boletos:0, comunicados:0, assembleias:0, encomendas:0, ocorrencias:0, demonstrativos:0, prestacao:0 });
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [notifs,       setNotifs]       = useState([]);

  const [boletos,      setBoletos]      = useState([]);
  const [filtBoleto,   setFiltBoleto]   = useState("aberto");
  const [boletoDetalhe, setBoletoDetalhe] = useState(null); // boleto aberto no modal
  const [copiado,       setCopiado]       = useState(false);
  const [pixCopiado,    setPixCopiado]    = useState(false);
  const [reservas,     setReservas]     = useState([]);
  const [comunicados,  setComunicados]  = useState([]);
  const [demonstrativos, setDemonstrativos] = useState([]);
  const [viewDemonstrativo, setViewDemonstrativo] = useState(null);
  const [assembleias,  setAssembleias]  = useState([]);
  const [ocorrencias,  setOcorrencias]  = useState([]);
  const [encomendas,   setEncomendas]   = useState([]);
  const [ocoSel,      setOcoSel]       = useState(null);
  const [respostaOco, setRespostaOco]  = useState("");
  const [prestacoes,   setPrestacoes]   = useState([]);
  const [prestacaoAtiva,setPrestacaoAtiva]=useState(null);
  const [loadingPrest, setLoadingPrest] = useState(false);
  const [autorizacoes, setAutorizacoes] = useState([]);
  const [moradores,    setMoradores]    = useState([]);
  const [historico,    setHistorico]    = useState([]);
  const [avisosPortaria, setAvisosPortaria] = useState([]);
  const [formAvisoPort, setFormAvisoPort] = useState({ tpAviso: "Outro", urgencia: "Normal", mensagem: "" });
  const [enviandoAviso, setEnviandoAviso] = useState(false);
  const [msgAvisoAberto, setMsgAvisoAberto] = useState({});
  const [enviandoMsgAviso, setEnviandoMsgAviso] = useState({});
  const [abaListaPortaria, setAbaListaPortaria] = useState("abertos");
  const [expandidosPortaria, setExpandidosPortaria] = useState({});

  const [showVotar,    setShowVotar]    = useState(false);
  const [showResultados, setShowResultados] = useState(false);
  const [resultados,   setResultados]   = useState(null);
  const [loadingRes,   setLoadingRes]   = useState(false);
  const [showAta,      setShowAta]      = useState(false);
  const [ataConteudo,  setAtaConteudo]  = useState(null);
  const [asmAtiva,     setAsmAtiva]     = useState(null);
  const [pautasAsm,    setPautasAsm]    = useState([]);
  const [loadingPautas,setLoadingPautas]= useState(false);
  const [votos,        setVotos]        = useState({});
  const [salvandoVoto, setSalvandoVoto] = useState(false);
  const [confirmandoPresencaId, setConfirmandoPresencaId] = useState(null);

  const [showOcoModal, setShowOcoModal] = useState(false);
  const [showNovaAuth, setShowNovaAuth] = useState(false);
  const [showNovoMor,  setShowNovoMor]  = useState(false);
  // Camera states for FotoPicker
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraTarget, setCameraTarget] = useState(null); // callback fn
  const cameraVideoRef = React.useRef(null);
  const [editMorador,  setEditMorador]  = useState(null);
  const [veiculos,     setVeiculos]     = useState([]);
  const [pets,         setPets]         = useState([]);
  const [showNovoVei,  setShowNovoVei]  = useState(false);
  const [showNovoPet,  setShowNovoPet]  = useState(false);
  const [editVeiculo,  setEditVeiculo]  = useState(null);
  const [editPet,      setEditPet]      = useState(null);
  const [savingVei,    setSavingVei]    = useState(false);
  const [savingPet,    setSavingPet]    = useState(false);
  const [formVei, setFormVei] = useState({idUnidade:"",placa:"",modelo:"",cor:"",tpVeiculo:"Carro",foto:null});
  const [formPet, setFormPet] = useState({idUnidade:"",nmPet:"",especie:"Cachorro",raca:"",cor:"",foto:null,observacao:""});
  const [qrVisible,    setQrVisible]    = useState({});

  const [formOco,  setFormOco]  = useState(E_OCO);
  const [ocoAnexosFiles, setOcoAnexosFiles] = useState([]);
  const [formAuth, setFormAuth] = useState(E_AUTH);
  const [formMor,  setFormMor]  = useState(E_MOR);

  // ── Perfil do condômino (avatar, senha, preferências) ──
  const [showPerfilModal, setShowPerfilModal] = useState(false);
  const [confirmRemoverAvatar, setConfirmRemoverAvatar] = useState(false);
  const [confirmLogoutAll, setConfirmLogoutAll] = useState(false);
  const [perfilData, setPerfilData] = useState(null);          // dados do GET /portal/perfil
  const [perfilTab, setPerfilTab]   = useState("foto");        // foto | senha | preferencias
  const [perfilSaving, setPerfilSaving] = useState(false);
  const [perfilMsg, setPerfilMsg] = useState(null);            // {type, text}
  const [senhaForm, setSenhaForm] = useState({ atual:"", nova:"", confirma:"" });
  const [showSenhas, setShowSenhas] = useState(false);

  // ── Notificação global (Banner) e confirmação (ConfirmModal) ──────────
  const [banner, setBanner] = useState(null);          // { tipo, mensagem }
  const [confirmacao, setConfirmacao] = useState(null); // { titulo, mensagem, textoOk, perigo, resolve }

  const notify = useCallback((mensagem, tipo = "info", ms = 4500) => {
    setBanner({ tipo, mensagem });
    if (ms > 0) setTimeout(() => setBanner(null), ms);
  }, []);

  const askConfirm = useCallback(({ titulo = "Confirmar?", mensagem = "Deseja prosseguir?", textoOk = "Confirmar", perigo = false } = {}) => {
    return new Promise((resolve) => {
      setConfirmacao({ titulo, mensagem, textoOk, perigo, resolve });
    });
  }, []);

  const onOcoAnexosChange = useCallback((e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    setOcoAnexosFiles((prev) => {
      const next = [...prev];
      for (const f of picked) {
        if (next.length >= OCO_ANEXO_MAX) {
          notify(`No máximo ${OCO_ANEXO_MAX} anexos por ocorrência.`, "warning");
          break;
        }
        if (f.size > OCO_ANEXO_MAX_BYTES) {
          notify(`${f.name}: máximo 2 MB por arquivo.`, "warning");
          continue;
        }
        const mt = f.type || "";
        if (!OCO_ANEXO_MIMES.has(mt)) {
          notify(`${f.name}: use JPG, PNG, GIF, WEBP ou PDF.`, "warning");
          continue;
        }
        next.push(f);
      }
      return next;
    });
  }, [notify]);

  /* ── load inicial ── */
  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const unis = await portalService.minhasUnidades();
      const uList = unis.data || [];
      setUnidades(uList);
      let chosen = null;
      if (uList.length > 0) {
        setCondoAtivo(prev => {
          chosen = prev ?? uList[0];
          return chosen;
        });
      }
      const chosenCondoId   = chosen?.idCondominio ?? (uList[0]?.idCondominio || null);
      const chosenUnidadeId = chosen?.idUnidade    ?? (uList[0]?.idUnidade    || null);
      const [alts, bols] = await Promise.all([
        portalService.alertas(chosenCondoId, chosenUnidadeId),
        portalService.boletos(chosenCondoId, chosenUnidadeId),
      ]);
      const alertasBase = alts.data||{};
      try {
        const rDemBadge = await portalService.demonstrativos(chosenCondoId).catch(()=>({data:[]}));
        alertasBase.prestacao = (rDemBadge.data||[]).length;
      } catch(e){}
      setAlertas(a=>({...a, ...alertasBase}));
      setBoletos(bols.data || []);

    } catch(e){ console.error(e); }
    setLoading(false);
  }, []); // eslint-disable-line

  useEffect(() => { carregar(); }, [carregar]);

  /* ── perfil: pré-carrega só pra exibir avatar no header ── */
  useEffect(() => {
    (async () => {
      try {
        const r = await portalService.buscarPerfil();
        setPerfilData(r.data || null);
      } catch (_e) { /* silencioso */ }
    })();
  }, []);

  /* ── perfil: carregar quando abre modal ── */
  const carregarPerfil = useCallback(async () => {
    setPerfilMsg(null);
    try {
      const r = await portalService.buscarPerfil();
      setPerfilData(r.data || null);
    } catch (e) {
      setPerfilMsg({ type: "danger", text: e?.message || "Erro ao carregar perfil." });
    }
  }, []);

  const abrirPerfil = useCallback((tab = "foto") => {
    setPerfilTab(tab);
    setSenhaForm({ atual: "", nova: "", confirma: "" });
    setShowSenhas(false);
    setPerfilMsg(null);
    setShowPerfilModal(true);
    carregarPerfil();
  }, [carregarPerfil]);

  const salvarAvatar = useCallback(async (avatarBase64) => {
    setPerfilSaving(true); setPerfilMsg(null);
    try {
      if (avatarBase64) {
        await portalService.atualizarAvatar(avatarBase64);
        setPerfilData(p => p ? { ...p, condomino: { ...(p.condomino || {}), avatar: avatarBase64 }, temAvatar: true } : p);
        setPerfilMsg({ type: "success", text: "Foto atualizada!" });
      } else {
        await portalService.removerAvatar();
        setPerfilData(p => p ? { ...p, condomino: { ...(p.condomino || {}), avatar: null }, temAvatar: false } : p);
        setPerfilMsg({ type: "success", text: "Foto removida!" });
      }
      // Sincroniza entre abas: outras abas escutam 'storage' e recarregam perfil
      try { localStorage.setItem("avatarUpdatedAt", String(Date.now())); } catch {}
    } catch (e) {
      setPerfilMsg({ type: "danger", text: e?.message || "Erro ao salvar foto." });
    }
    setPerfilSaving(false);
  }, []);

  // Sincronização inter-tab do avatar: quando outra aba muda, recarrega perfil
  useEffect(() => {
    const onStorage = (ev) => {
      if (ev.key === "avatarUpdatedAt") {
        portalService.buscarPerfil()
          .then(r => setPerfilData(r.data || null))
          .catch(() => {});
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const salvarSenha = useCallback(async () => {
    setPerfilMsg(null);
    const { atual, nova, confirma } = senhaForm;
    if (!atual || !nova) {
      setPerfilMsg({ type: "warning", text: "Preencha a senha atual e a nova senha." });
      return;
    }
    if (nova.length < 6) {
      setPerfilMsg({ type: "warning", text: "A nova senha deve ter ao menos 6 caracteres." });
      return;
    }
    if (nova !== confirma) {
      setPerfilMsg({ type: "warning", text: "Confirmação não confere com a nova senha." });
      return;
    }
    setPerfilSaving(true);
    try {
      await portalService.trocarSenha(atual, nova);
      setSenhaForm({ atual: "", nova: "", confirma: "" });
      setPerfilMsg({ type: "success", text: "Senha alterada com sucesso!" });
    } catch (e) {
      setPerfilMsg({ type: "danger", text: e?.message || "Erro ao trocar senha." });
    }
    setPerfilSaving(false);
  }, [senhaForm]);

  const salvarPreferencias = useCallback(async () => {
    setPerfilMsg(null);
    setPerfilSaving(true);
    try {
      const c = perfilData?.condomino || {};
      await portalService.atualizarPerfil({
        dtNascimento:    c.dtNascimento ? String(c.dtNascimento).split("T")[0] : null,
        aniversarioOptIn: !!c.aniversarioOptIn,
      });
      setPerfilMsg({ type: "success", text: "Preferências salvas!" });
    } catch (e) {
      setPerfilMsg({ type: "danger", text: e?.message || "Erro ao salvar preferências." });
    }
    setPerfilSaving(false);
  }, [perfilData]);

  // Ensure condoAtivo is set when unidades loads
  useEffect(() => {
    if (unidades.length > 0 && !condoAtivo) {
      setCondoAtivo(unidades[0]);
    }
  }, [unidades]); // eslint-disable-line

  // Recarrega a aba atual sempre que a unidade ativa muda (após o load inicial),
  // garantindo que filtros por unidade sejam reaplicados.
  const skipFirstReload = useRef(true);
  useEffect(() => {
    if (skipFirstReload.current) { skipFirstReload.current = false; return; }
    if (!condoAtivo?.idUnidade) return;
    if (aba && aba !== "inicio") {
      carregarAba(aba);
    }
  }, [condoAtivo?.idUnidade, condoAtivo?.idCondominio]); // eslint-disable-line

  /* ── troca de condomínio/unidade: recarrega dados ── */
  const trocarCondo = useCallback(async (u) => {
    const condoMudou = !condoAtivo || String(u.idCondominio) !== String(condoAtivo.idCondominio);
    setCondoAtivo(u);
    setShowCondoSel(false);
    // Se mudou o condomínio, volta para "inicio". Se foi só troca de unidade, mantém a aba atual.
    if (condoMudou) setAba("inicio");
    setLoading(true);
    // Limpa dados antigos para evitar exibir lista da unidade anterior
    setReservas([]); setOcorrencias([]); setComunicados([]); setAssembleias([]);
    setEncomendas([]); setMoradores([]); setVeiculos([]); setPets([]);
    setPrestacoes([]); setDemonstrativos([]); setAutorizacoes([]); setHistorico([]);
    try {
      const [alts, bols] = await Promise.all([
        portalService.alertas(u.idCondominio, u.idUnidade),
        portalService.boletos(u.idCondominio, u.idUnidade),
      ]);
      const alertasBase = alts.data||{};
      try {
        const rDemBadge = await portalService.demonstrativos(u.idCondominio).catch(()=>({data:[]}));
        alertasBase.prestacao = (rDemBadge.data||[]).length;
      } catch(e){}
      setAlertas(a=>({...a, ...alertasBase}));
      setBoletos(bols.data || []);

    } catch(e){ console.error(e); }
    setLoading(false);
  }, [condoAtivo]);

  /* ── carregar por aba ── */
  const carregarAba = useCallback(async (novaAba) => {
    setAba(novaAba);
    // Clear badge for the opened tab
    if (novaAba==="comunicados")  setAlertas(a=>({...a,comunicados:0}));
    if (novaAba==="ocorrencias")  setAlertas(a=>({...a,ocorrencias:0}));
    if (novaAba==="assembleias")  setAlertas(a=>({...a,assembleias:0}));
    if (novaAba==="boletos")      setAlertas(a=>({...a,boletos:0}));
    if (novaAba==="encomendas"||novaAba==="portaria") setAlertas(a=>({...a,encomendas:0}));
    // scroll tab into view
    setTimeout(() => {
      const el = tabsRef.current?.querySelector(`[data-tab="${novaAba}"]`);
      el?.scrollIntoView({ behavior:"smooth", block:"nearest", inline:"center" });
    }, 50);
    const c = condoAtivo;
    if (!c) return;
    const idCondo = c.idCondominio;
    const idUnid  = c.idUnidade;
    try {
      // Filtra por condomínio + unidade
      if (novaAba==="boletos")     { const r=await portalService.boletos(idCondo, idUnid);     setBoletos(r.data||[]); }
      if (novaAba==="encomendas")  { const r=await portalService.encomendas(idCondo, idUnid);  setEncomendas(r.data||[]); setAlertas(a=>({...a,encomendas:0})); }
      if (novaAba==="ocorrencias") { const r=await portalService.ocorrencias(idCondo, idUnid); setOcorrencias(r.data||[]); }
      if (novaAba==="moradores")   { const r=await portalService.moradores(idCondo, idUnid);   setMoradores(r.data||[]); }
      if (novaAba==="veiculos")    { const r=await portalService.veiculos(idCondo, idUnid);    setVeiculos(r.data||[]); }
      if (novaAba==="pets")        { const r=await portalService.pets(idCondo, idUnid);        setPets(r.data||[]); }
      if (novaAba==="autorizacoes"){ const r=await portariaService.preautorizacoes({idCondominio:idCondo, idUnidade:idUnid}); setAutorizacoes(r.data||[]); }
      if (novaAba==="historico")   { const r=await portariaService.registros({idCondominio:idCondo, idUnidade:idUnid, limit:30}); setHistorico(r.data||[]); }
      if (novaAba==="portaria") {
        const r = await portalService.avisosPortaria(idCondo, idUnid);
        setAvisosPortaria(r.data || []);
      }

      // Filtra apenas por condomínio (escopo condomínio inteiro)
      if (novaAba==="comunicados") { const r=await portalService.comunicados(idCondo, idUnid);  setComunicados(r.data||[]); }
      if (novaAba==="assembleias") { const r=await portalService.assembleias(idCondo, idUnid);  setAssembleias(r.data||[]); }
      if (novaAba==="prestacao") {
        try {
          const [rPrest, rDem] = await Promise.all([
            portalService.prestacoes(idCondo).catch(()=>({data:[]})),
            portalService.demonstrativos(idCondo).catch(()=>({data:[]}))
          ]);
          const meses=["Janeiro","Fevereiro","Março","Abril","Maio","Junho",
                       "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
          const demComoPresta = (rDem.data||[]).map(d=>{
            const [ano,mes] = (d.competencia||"").split("-");
            return { ...d,
              titulo:`Prestação de Contas ${meses[parseInt(mes)-1]||mes}/${ano}`,
              dtPublicacao:d.dtFechamento, tpItem:"prestacao-fechamento" };
          });
          setPrestacoes([...(rPrest.data||[]), ...demComoPresta]);
          setPrestacaoAtiva(null);
          setAlertas(a=>({...a, prestacao:0}));
        } catch(e){ console.error(e); }
      }
      if (novaAba==="demonstrativos") {
        try { const r = await portalService.demonstrativos(idCondo); setDemonstrativos(r.data||[]); } catch(e){}
      }
      // "reservas" — gerenciada pelo componente ReservasPortal internamente
    } catch(e){ console.error(e); }
  }, [condoAtivo]);

  /* ── socket ── */
  const addNotif = (icon, cor, bg, texto) =>
    setNotifs(n=>[{ id:Date.now(), icon, cor, bg, texto },...n.slice(0,4)]);

  useSocket(condoAtivo?.idCondominio, {
    novo_comunicado:     d => { addNotif("bi-megaphone","#1D4ED8","#EFF6FF",`Comunicado: ${d.titulo||""}`); setAlertas(a=>({...a,comunicados:(a.comunicados||0)+1})); },
    nova_ocorrencia:     d => { addNotif("bi-exclamation-circle","#B45309","#FEF9C3",`Ocorrência: ${d.titulo||""}`); setAlertas(a=>({...a,ocorrencias:(a.ocorrencias||0)+1})); },
    visita_chegou:       d => { addNotif("bi-person-badge","#7C3AED","#F5F3FF",`👤 ${d.tpVisita||"Visitante"}: ${d.nmVisitante} chegou`); },
    encomenda_chegou:    d => { addNotif("bi-box-seam","#C2410C","#FFF7ED",`📦 Encomenda chegou: ${d.descricao||""}`); setAlertas(a=>({...a,encomendas:(a.encomendas||0)+1})); },
    assembleia_convocada:d => { addNotif("bi-people","#166534","#DCFCE7",`Assembleia: ${d.titulo||""}`); setAlertas(a=>({...a,assembleias:(a.assembleias||0)+1})); },
    ocorrencia_portaria: d => { if(d.gravidade==="Alta") addNotif("bi-exclamation-triangle-fill","#991B1B","#FEE2E2",`Ocorrência alta: ${d.tpOcorrencia||""}`); },
    boleto_pago:         d => {
      addNotif("bi-check-circle-fill","#065F46","#DCFCE7",`✅ Pagamento confirmado: ${fmtVal(d.vlrPago||0)}`);
      setAlertas(a=>({ ...a, boletos: Math.max(0, (a.boletos||0)-1) }));
      // Atualiza o boleto localmente
      setBoletos(arr => arr.map(b => b.id === d.idBoleto
        ? { ...b, statusBoleto:"Pago", dtPagamento:d.dtPagamento }
        : b));
      // Se o modal está aberto nesse boleto, atualiza
      setBoletoDetalhe(b => b && b.id === d.idBoleto
        ? { ...b, statusBoleto:"Pago", dtPagamento:d.dtPagamento }
        : b);
    },
  });

  /* ── votação ── */
  const abrirVotacao = async (asm) => {
    setAsmAtiva(asm); setShowVotar(true); setLoadingPautas(true);
    try { const r=await portalService.assembleiaPautas(asm.id); setPautasAsm(r.data?.pautas||r.data||[]); setVotos({}); }
    catch(e){ notify("Erro ao carregar pautas.", "danger"); }
    setLoadingPautas(false);
  };
  const handleVotar = async () => {
    const idUnidade = activeCondo?.idUnidade;
    if (!idUnidade) { notify("Selecione sua unidade para votar.", "warning"); return; }
    const pautasComVoto = Object.keys(votos).filter(k => votos[k]);
    if (!pautasComVoto.length) { notify("Selecione pelo menos um voto.", "warning"); return; }
    setSalvandoVoto(true);
    try {
      let erros = [];
      for (const idPauta of pautasComVoto) {
        try {
          await portalService.votar(asmAtiva.id, {
            idPauta: parseInt(idPauta),
            idUnidade,
            voto: votos[idPauta],
          });
        } catch(e) {
          if (e.message?.includes("ALREADY_VOTED")) continue; // já votou, ok
          erros.push(e.message);
        }
      }
      if (erros.length) notify("Alguns votos não puderam ser registrados: " + erros.join(" / "), "danger");
      else {
        setShowVotar(false);
        notify("Votos registrados com sucesso!", "success");
      }
      const r = await portalService.assembleias(condoAtivo?.idCondominio, condoAtivo?.idUnidade);
      setAssembleias(r.data||[]);
    } catch(e){ notify(e.message || "Erro ao registrar voto.", "danger"); }
    setSalvandoVoto(false);
  };

  /* ── prestação ── */
  const abrirPrestacao = async (id) => {
    setLoadingPrest(true);
    try { const r=await portalService.prestacaoDetalhe(id); setPrestacaoAtiva(r.data); }
    catch(e){ notify("Erro ao carregar prestação.", "danger"); }
    setLoadingPrest(false);
  };
  const imprimirPrestacao = (html) => {
    const w=window.open("","_blank","width=900,height=700");
    w.document.write(`<!DOCTYPE html><html><head><title>Prestação de Contas</title>
      <style>@media print{.np{display:none}}body{font-family:Arial}</style></head><body>
      <div class="np" style="padding:12px;text-align:center;background:#f1f5f9">
        <button onclick="window.print()" style="background:#1B3A6B;color:#fff;border:none;padding:8px 20px;border-radius:6px;cursor:pointer">🖨️ Imprimir / PDF</button>
        <button onclick="window.close()" style="margin-left:10px;padding:8px 20px;border:1px solid #ccc;border-radius:6px;cursor:pointer">Fechar</button>
      </div>${html}</body></html>`);
    w.document.close(); w.focus();
  };

  const handleCopiarLinha = (linha) => {
    navigator.clipboard.writeText(linha).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    });
  };

  const handleCopiarPix = (payload) => {
    if (!payload) return;
    navigator.clipboard.writeText(payload).then(() => {
      setPixCopiado(true);
      setTimeout(() => setPixCopiado(false), 2500);
    }).catch(() => {
      // Fallback antigo
      const ta = document.createElement("textarea");
      ta.value = payload; document.body.appendChild(ta);
      ta.select(); document.execCommand("copy");
      document.body.removeChild(ta);
      setPixCopiado(true);
      setTimeout(() => setPixCopiado(false), 2500);
    });
  };

  const handleCompartilharBoleto = async (boleto) => {
    if (!boleto?.tokenPublico) return;
    const BASE = getApiBaseUrl().replace(/\/api$/, "");
    const url = `${BASE}/boleto/${boleto.tokenPublico}`;
    const titulo = `Boleto ${boleto.competencia || ""} — ${boleto.nmFantasia || boleto.nmCondominio || ""}`;
    const texto = `🧾 *Boleto* ${boleto.competencia || ""}\n💰 Valor: ${fmtVal(boleto.vlrBoleto)}\n📅 Vencimento: ${fmtD(boleto.dtVencimento)}\n\n${url}`;

    // Tenta Web Share API (mobile)
    if (navigator.share) {
      try {
        await navigator.share({ title: titulo, text: texto, url });
        return;
      } catch (e) { if (e?.name === "AbortError") return; }
    }
    // Fallback: WhatsApp Web/desktop
    const waUrl = `https://wa.me/?text=${encodeURIComponent(texto)}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
  };

  const handleImprimirBoleto = (id) => {
    const BASE = getApiBaseUrl();
    const token = localStorage.getItem("accessToken");
    fetch(`${BASE}/boletos/${id}/imprimir`, { headers:{ Authorization:`Bearer ${token}` }})
      .then(r => r.json())
      .then(d => { if(d.html) { const w=window.open("","_blank"); w.document.write(d.html); w.document.close(); } })
      .catch(() => notify("Erro ao carregar boleto.", "danger"));
  };

  /* ── boletos filtrados ── */
  const boletosFilt = boletos.filter(b => {
    if (filtBoleto==="aberto")  return ["Gerado","Registrado"].includes(b.statusBoleto);
    if (filtBoleto==="vencido") return b.statusBoleto==="Vencido";
    if (filtBoleto==="pago")    return b.statusBoleto==="Pago";
    return true;
  });
  const boletosAbertos  = boletos.filter(b=>["Gerado","Registrado","Vencido"].includes(b.statusBoleto)).length;
  const boletosVencidos = boletos.filter(b=>b.statusBoleto==="Vencido").length;

  /* ── loading screen ── */

  const FotoPicker = ({foto, onChange, label="Foto"}) => {
    const fileRef = React.useRef(null);
    const vidRef  = React.useRef(null);
    const [showCam, setShowCam] = React.useState(false);
    const [stream,  setStream]  = React.useState(null);

    const handleFile = e => {
      const f = e.target.files[0]; if(!f) return;
      const r = new FileReader();
      r.onload = ev => onChange(ev.target.result);
      r.readAsDataURL(f);
    };

    const abrirCam = async () => {
      try {
        const s = await openCamera();
        setStream(s); setShowCam(true);
        setTimeout(() => { if(vidRef.current) vidRef.current.srcObject = s; }, 100);
      } catch(e) {
        const msg = e.name==="NotAllowedError"
          ? "Permissão de câmera negada. Verifique as configurações do navegador."
          : "Câmera não disponível: "+e.message;
        notify(msg, "warning");
      }
    };

    const tirarFoto = () => {
      if(!vidRef.current) return;
      const canvas = document.createElement("canvas");
      canvas.width  = vidRef.current.videoWidth  || 640;
      canvas.height = vidRef.current.videoHeight || 480;
      canvas.getContext("2d").drawImage(vidRef.current, 0, 0);
      onChange(canvas.toDataURL("image/jpeg", 0.85));
      fecharCam();
    };

    const fecharCam = () => {
      if(stream) stream.getTracks().forEach(t=>t.stop());
      setStream(null); setShowCam(false);
    };

    return (
      <div>
        <label style={{fontSize:12,fontWeight:600,display:"block",marginBottom:6}}>{label}</label>
        {foto ? (
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <img src={foto} alt="foto"
              style={{width:72,height:72,borderRadius:8,objectFit:"cover",
                cursor:"pointer",border:"2px solid #E2E8F0"}}
              onClick={()=>{
                const ov=document.createElement("div");
                ov.style.cssText="position:fixed;inset:0;background:rgba(0,0,0,.85);z-index:99999;display:flex;align-items:center;justify-content:center;cursor:zoom-out";
                const im=document.createElement("img"); im.src=foto;
                im.style.cssText="max-width:90vw;max-height:90vh;border-radius:10px";
                ov.appendChild(im); ov.onclick=()=>document.body.removeChild(ov);
                document.body.appendChild(ov);
              }}/>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <button type="button" className="btn btn-outline-secondary btn-sm"
                onClick={()=>fileRef.current?.click()}>
                <i className="bi bi-arrow-repeat me-1"/>Trocar
              </button>
              <button type="button" className="btn btn-outline-danger btn-sm"
                onClick={()=>onChange(null)}>
                <i className="bi bi-trash3 me-1"/>Remover
              </button>
            </div>
          </div>
        ) : (
          <div style={{display:"flex",gap:8}}>
            <button type="button" className="btn btn-outline-primary btn-sm"
              onClick={abrirCam}>
              <i className="bi bi-camera me-1"/>Câmera
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm"
              onClick={()=>fileRef.current?.click()}>
              <i className="bi bi-folder2-open me-1"/>Arquivo
            </button>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleFile}/>
        {showCam && (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.85)",zIndex:9999,
            display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12}}>
            <video ref={vidRef} autoPlay playsInline muted
              style={{width:"min(480px,90vw)",borderRadius:12,border:"3px solid #fff"}}/>
            <div style={{display:"flex",gap:10}}>
              <button className="btn btn-success"
                onClick={tirarFoto}>
                <i className="bi bi-camera-fill me-1"/>Tirar Foto
              </button>
              <button className="btn btn-outline-light"
                onClick={fecharCam}>
                <i className="bi bi-x-lg me-1"/>Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading && unidades.length === 0) return (
    <div style={{minHeight:"100vh",background:"var(--bg-page)",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{textAlign:"center"}}>
        <span className="spinner-border text-primary" style={{width:40,height:40}}/>
        <div style={{marginTop:12,color:"var(--gray-500)",fontSize:14}}>Carregando...</div>
      </div>
    </div>
  );

  // Fallback: ensure condoAtivo is set if unidades loaded but state not yet updated
  // Always derive activeCondo from state or first unit
  const activeCondo = condoAtivo
    ?? (unidades.length > 0 ? unidades[0] : null);

  // Group units by condomínio
  const condosMap = unidades.reduce((acc, u) => {
    const key = u.idCondominio;
    if (!acc[key]) acc[key] = { idCondominio:u.idCondominio, nmCondominio:u.nmCondominio, nmFantasia:u.nmFantasia, unidades:[] };
    acc[key].unidades.push(u);
    return acc;
  }, {});
  const condosList  = Object.values(condosMap); // unique condos
  const multiCondo  = condosList.length > 1;
  // Units of the active condo
  const unidadesAtivas = activeCondo
    ? unidades.filter(u => String(u.idCondominio) === String(activeCondo.idCondominio))
    : unidades;
  const multiUnidade = unidadesAtivas.length > 1;
  const condos = condosList; // for the dropdown

  return (
    <div className="pcp-page">

      {/* ── HEADER ── */}
      <div style={{background:"#1B3A6B",padding:"0 16px",display:"flex",alignItems:"center",
        justifyContent:"space-between",height:56,flexShrink:0,
        boxShadow:"0 2px 8px rgba(0,0,0,.2)"}}>

        {/* Logo */}
        <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
          <i className="bi bi-buildings-fill" style={{color:"#fff",fontSize:18}}/>
          <span style={{color:"#fff",fontWeight:700,fontSize:14}}>eCondo</span>
        </div>

        {/* ── CONDOMÍNIO ATIVO ── */}
        <div style={{flex:1,minWidth:0,display:"flex",justifyContent:"center",
          padding:"0 8px",position:"relative"}}>

          {/* Pill sempre visível */}
          <div
            onClick={()=>setShowCondoSel(s=>!s)}
            style={{
              background:"rgba(255,255,255,.2)",
              border:"1px solid rgba(255,255,255,.4)",
              borderRadius:10,padding:"5px 12px",cursor:"pointer",
              display:"flex",alignItems:"center",gap:8,
              maxWidth:"calc(100vw - 220px)",minWidth:0,
            }}>
            <i className="bi bi-building-fill"
              style={{color:"#FCD34D",fontSize:14,flexShrink:0}}/>
            <div style={{overflow:"hidden",flex:1,minWidth:0}}>
              <div style={{color:"#fff",fontWeight:700,fontSize:13,
                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {activeCondo
                  ? (activeCondo.nmFantasia || activeCondo.nmCondominio || "—")
                  : "Carregando..."}
              </div>
              {activeCondo && (
                <div style={{color:"rgba(255,255,255,.75)",fontSize:11}}>
                  Un.&nbsp;{activeCondo.nmUnidade||"?"}
                  {activeCondo.bloco ? ` / Bl. ${activeCondo.bloco}` : ""}
                </div>
              )}
            </div>
            <i className="bi bi-chevron-down"
              style={{color:"rgba(255,255,255,.7)",fontSize:10,flexShrink:0,
                transform:showCondoSel?"rotate(180deg)":"none",
                transition:"transform .2s"}}/>
          </div>

          {/* Dropdown */}
          {showCondoSel && (
            <div className="pcp-condo-dropdown"
              onClick={e=>e.stopPropagation()}
              style={{top:"calc(100% + 6px)"}}>

              {!activeCondo ? (
                <div style={{padding:16,textAlign:"center",color:"var(--gray-400)",fontSize:13}}>
                  Carregando...
                </div>
              ) : !multiCondo && !multiUnidade ? (
                /* 1 condo, 1 unidade — só info */
                <div style={{padding:"12px 14px"}}>
                  {usuario?.clienteLogo && (
                    <img src={usuario.clienteLogo} alt="Logo"
                      style={{height:40,maxWidth:120,objectFit:"contain",marginBottom:6,display:"block"}}/>
                  )}
                  <div style={{fontWeight:700,fontSize:13,color:"var(--text-primary)"}}>
                    {activeCondo.nmFantasia||activeCondo.nmCondominio}
                  </div>
                  <div style={{fontSize:12,color:"var(--gray-500)",marginTop:2}}>
                    Unidade {activeCondo.nmUnidade}
                    {activeCondo.bloco?` / Bl. ${activeCondo.bloco}`:""}
                  </div>
                  <div style={{fontSize:11,color:"#166534",marginTop:4}}>
                    <i className="bi bi-check-circle-fill me-1"/>Unidade ativa
                  </div>
                </div>
              ) : multiCondo ? (
                /* Múltiplos condomínios — agrupa */
                condos.map(condo=>(
                  <div key={condo.idCondominio}>
                    <div style={{padding:"7px 14px",fontSize:11,fontWeight:700,
                      color:"#1B3A6B",background:"var(--bg-surface-alt)",
                      borderBottom:"1px solid var(--border-subtle)"}}>
                      <i className="bi bi-buildings me-1"/>
                      {condo.nmFantasia||condo.nmCondominio}
                    </div>
                    {condo.unidades.map(u=>{
                      const sel=String(activeCondo?.idUnidade)===String(u.idUnidade);
                      return (
                        <div key={u.idUnidade}
                          className={`pcp-condo-option${sel?" sel":""}`}
                          onClick={()=>{trocarCondo(u);setShowCondoSel(false);}}>
                          <i className={`bi ${sel?"bi-check-circle-fill":"bi-circle"}`}
                            style={{color:sel?"#1B3A6B":"#CBD5E1",fontSize:14,flexShrink:0}}/>
                          <div style={{flex:1,fontSize:13,fontWeight:sel?700:400}}>
                            Un. {u.nmUnidade}{u.bloco?` / Bl. ${u.bloco}`:""}
                          </div>
                          {sel&&<span style={{fontSize:10,fontWeight:700,color:"#1B3A6B",
                            background:"var(--bg-highlight)",padding:"2px 8px",borderRadius:8}}>Ativa</span>}
                        </div>
                      );
                    })}
                  </div>
                ))
              ) : (
                /* 1 condo, múltiplas unidades */
                <div>
                  <div style={{padding:"8px 14px",fontSize:11,fontWeight:700,
                    color:"var(--gray-400)",textTransform:"uppercase",letterSpacing:.5,
                    borderBottom:"1px solid var(--border-subtle)"}}>
                    {activeCondo.nmFantasia||activeCondo.nmCondominio}
                  </div>
                  {unidadesAtivas.map(u=>{
                    const sel=String(activeCondo?.idUnidade)===String(u.idUnidade);
                    return (
                      <div key={u.idUnidade}
                        className={`pcp-condo-option${sel?" sel":""}`}
                        onClick={()=>{trocarCondo(u);setShowCondoSel(false);}}>
                        <i className={`bi ${sel?"bi-check-circle-fill":"bi-circle"}`}
                          style={{color:sel?"#1B3A6B":"#CBD5E1",fontSize:14,flexShrink:0}}/>
                        <div style={{flex:1,fontSize:13,fontWeight:sel?700:400}}>
                          Un. {u.nmUnidade}{u.bloco?` / Bl. ${u.bloco}`:""}
                        </div>
                        {sel&&<span style={{fontSize:10,fontWeight:700,color:"#1B3A6B",
                          background:"var(--bg-highlight)",padding:"2px 8px",borderRadius:8}}>Ativa</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

                {/* Actions */}
        <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
          {notifs.length>0 && (
            <div style={{position:"relative",cursor:"pointer"}} onClick={()=>setNotifs([])}>
              <i className="bi bi-bell-fill" style={{color:"#FCD34D",fontSize:18}}/>
              <span style={{position:"absolute",top:-5,right:-5,background:"#EF4444",
                color:"#fff",fontSize:9,borderRadius:10,padding:"1px 4px",fontWeight:700}}>
                {notifs.length}
              </span>
            </div>
          )}
          <span style={{color:"rgba(255,255,255,.8)",fontSize:12,display:"none"}}
            className="d-sm-inline">
            {usuario?.nome?.split(" ")[0]}
          </span>
          <ThemeToggle variant="onDark" />
          <Avatar
            src={perfilData?.condomino?.avatar}
            nome={perfilData?.condomino?.nmCompleto || usuario?.nome || ""}
            size={34}
            borderColor="rgba(255,255,255,.4)"
            onClick={()=>abrirPerfil("foto")}
            title="Meu perfil"
          />
          <button type="button" title="Sair" onClick={sair}
            style={{background:"rgba(255,255,255,.15)",border:"none",
            color:"#fff",borderRadius:6,padding:"4px 8px",fontSize:12,cursor:"pointer",
            flexShrink:0,whiteSpace:"nowrap"}}>
            <i className="bi bi-box-arrow-right" aria-hidden/>
            <span style={{marginLeft:4}}>Sair</span>
          </button>
        </div>
      </div>

      {/* ── NOTIFICAÇÕES ── */}
      {notifs.length>0 && (
        <div style={{background:"var(--bg-surface)",borderBottom:"1px solid var(--border-color)",
          padding:"8px 16px",display:"flex",gap:8,overflowX:"auto",flexShrink:0}}>
          {notifs.map(n=>(
            <div key={n.id} style={{display:"flex",alignItems:"center",gap:6,
              background:n.bg,borderRadius:8,padding:"5px 10px",flexShrink:0,fontSize:12,
              color:n.cor,fontWeight:600}}>
              <i className={`bi ${n.icon}`}/>
              <span style={{maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {n.texto}
              </span>
              <button onClick={()=>setNotifs(ns=>ns.filter(x=>x.id!==n.id))}
                style={{background:"none",border:"none",color:n.cor,cursor:"pointer",
                  fontWeight:700,fontSize:14,lineHeight:1,padding:0}}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* ── NAV TABS ── */}
      <div ref={tabsRef} className="pcp-tabs"
        style={{borderBottom:"1px solid var(--border-color)",
          display:"flex",overflowX:"auto",flexShrink:0}}>
        {TABS.map(t=>{
          const badge = t.id==="boletos"      ? alertas.boletos
            : t.id==="comunicados"  ? alertas.comunicados
            : t.id==="assembleias"  ? alertas.assembleias
            : t.id==="ocorrencias"  ? alertas.ocorrencias
            : t.id==="demonstrativos" ? alertas.demonstrativos
            : t.id==="prestacao"      ? alertas.prestacao
            : (t.id==="encomendas"||t.id==="portaria") ? alertas.encomendas : 0;
          return (
            <button key={t.id} data-tab={t.id}
              className={`pcp-tab-btn${aba===t.id?" active":""}`}
              onClick={()=>carregarAba(t.id)}>
              <i className={`bi ${t.icon}`}/>
              <span>{t.label}</span>
              {badge>0 && (
                <span style={{background:"#EF4444",color:"#fff",fontSize:10,
                  borderRadius:10,padding:"1px 5px",fontWeight:700}}>{badge}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── CONTEÚDO ── */}
      <div className="pcp-content"
        style={{flex:1,padding:0,width:"100%",boxSizing:"border-box"}}
        onClick={()=>showCondoSel&&setShowCondoSel(false)}>
        <div style={{maxWidth:900,margin:"0 auto",padding:16,minHeight:"100%"}}>
        <PwaInstallHint />

        {/* INÍCIO */}
        {aba==="inicio" && (
          <div>
            <h2 style={{fontSize:17,fontWeight:700,color:"var(--text-primary)",marginBottom:16}}>
              Olá, {usuario?.nome?.split(" ")[0]}! 👋
            </h2>
            <div className="pcp-kpi-grid"
              style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",
                gap:12,marginBottom:20}}>
              {[
                {label:"Em aberto",  value:boletosAbertos,          icon:"bi-receipt",           color:"#1D4ED8",bg:"#EFF6FF",aba:"boletos"},
                {label:"Vencidos",   value:boletosVencidos,         icon:"bi-exclamation-circle", color:boletosVencidos>0?"#991B1B":"#64748B",bg:boletosVencidos>0?"#FEE2E2":"#F1F5F9",aba:"boletos"},
                {label:"Comunicados",value:alertas.comunicados||0,  icon:"bi-megaphone",          color:"#7C3AED",bg:"#F5F3FF",aba:"comunicados"},
                {label:"Assembleias",value:alertas.assembleias||0,  icon:"bi-people",             color:"#0891B2",bg:"#ECFEFF",aba:"assembleias"},
              ].map(c=>(
                <div key={c.label} className="pcp-card"
                  style={{padding:14,cursor:"pointer"}} onClick={()=>carregarAba(c.aba)}>
                  <div style={{display:"flex",gap:10,alignItems:"center"}}>
                    <div style={{width:38,height:38,borderRadius:10,background:c.bg,
                      display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <i className={`bi ${c.icon}`} style={{fontSize:17,color:c.color}}/>
                    </div>
                    <div>
                      <div style={{fontSize:22,fontWeight:700,color:"var(--text-primary)",lineHeight:1}}>{c.value}</div>
                      <div style={{fontSize:11,color:"var(--gray-400)"}}>{c.label}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <h3 style={{fontSize:13,fontWeight:700,color:"var(--gray-500)",marginBottom:10,
              textTransform:"uppercase",letterSpacing:.5}}>Acesso rápido</h3>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:10}}>
              {[
                {icon:"bi-qr-code",           label:"Autorizar visitante",aba:"autorizacoes"},
                {icon:"bi-calendar-check",    label:"Fazer reserva",      aba:"reservas"},
                {icon:"bi-exclamation-circle",label:"Abrir ocorrência",   aba:"ocorrencias"},
                {icon:"bi-clock-history",     label:"Ver histórico",      aba:"historico"},
                {icon:"bi-shield-check",      label:"Aviso à portaria",   aba:"portaria"},
              ].map(a=>(
                <button key={a.aba} onClick={()=>carregarAba(a.aba)}
                  className="pcp-card"
                  style={{border:"none",borderRadius:10,padding:"14px 10px",cursor:"pointer",
                    textAlign:"center",display:"flex",flexDirection:"column",
                    alignItems:"center",gap:8,width:"100%"}}>
                  <i className={`bi ${a.icon}`} style={{fontSize:22,color:"#1B3A6B"}}/>
                  <span style={{fontSize:12,color:"#374151",fontWeight:600}}>{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* BOLETOS */}
        {aba==="boletos" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
              marginBottom:14,flexWrap:"wrap",gap:10}}>
              <h3 style={{margin:0,fontSize:16,fontWeight:700}}>Meus Boletos</h3>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {[["aberto","Em Aberto"],["vencido","Vencidos"],["pago","Pagos"],["","Todos"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setFiltBoleto(v)}
                    style={{padding:"5px 12px",borderRadius:8,fontSize:12,fontWeight:600,
                      cursor:"pointer",border:`1px solid ${filtBoleto===v?"#1B3A6B":"#E2E8F0"}`,
                      background:filtBoleto===v?"#1B3A6B":"#fff",
                      color:filtBoleto===v?"#fff":"#64748B"}}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {boletosFilt.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                <i className="bi bi-receipt" style={{fontSize:36,display:"block",marginBottom:8}}/>
                Nenhum boleto
              </div>
            ) : boletosFilt.map(b=>(
              <div key={b.id} className="pcp-card"
                style={{padding:14,marginBottom:8,borderLeft:`4px solid ${STC[b.statusBoleto]||"#E2E8F0"}`,cursor:"pointer"}}
                onClick={()=>setBoletoDetalhe(b)}>
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"center",flexWrap:"wrap",gap:8}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:14,
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {b.nmFantasia||b.nmCondominio} — {b.competencia||""}
                    </div>
                    <div style={{fontSize:12,color:"var(--gray-400)",marginTop:2}}>
                      Vencimento: {fmtD(b.dtVencimento)}{b.unidade?` · Un. ${b.unidade}`:""}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
                    <span style={{fontSize:15,fontWeight:700,color:STC[b.statusBoleto]||"#1E293B"}}>
                      {fmtVal(b.vlrBoleto)}
                    </span>
                    <span style={{fontSize:11,fontWeight:700,padding:"3px 8px",borderRadius:10,
                      background:STB[b.statusBoleto]||"#F1F5F9",color:STC[b.statusBoleto]||"#64748B"}}>
                      {b.statusBoleto}
                    </span>
                    {["Gerado","Registrado","Vencido"].includes(b.statusBoleto) && (
                      <button className="btn btn-sm" onClick={e=>{e.stopPropagation();setBoletoDetalhe(b);}}
                        style={{fontSize:11,padding:"2px 8px",background:"#1B3A6B",color:"#fff",border:"none",borderRadius:6}}>
                        Ver
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* RESERVAS */}
        {aba==="reservas" && (
          <ReservasPortal
            activeCondo={activeCondo}
            unidades={unidades}
            portalService={portalService}
          />
        )}

        {/* ASSEMBLEIAS */}
        {aba==="assembleias" && (
          <div>
            <h3 style={{fontSize:16,fontWeight:700,marginBottom:14}}>Assembleias</h3>
            {assembleias.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                <i className="bi bi-people" style={{fontSize:36,display:"block",marginBottom:8}}/>Nenhuma assembleia
              </div>
            ) : assembleias.map(a=>(
              <div key={a.id} className="pcp-card" style={{padding:14,marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"flex-start",flexWrap:"wrap",gap:8}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:14}}>{a.titulo||a.tpAssembleia}</div>
                    <div style={{fontSize:12,color:"var(--gray-400)",marginTop:2}}>
                      {fmtD(a.dtAssembleia)}{a.hrInicio?` às ${String(a.hrInicio).substring(0,5)}`:""} · {a.local||"Online"}
                    </div>
                    <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:10,
                      display:"inline-block",marginTop:6,
                      background:a.status==="Aberta"?"#DCFCE7":"#F1F5F9",
                      color:a.status==="Aberta"?"#166534":"#64748B"}}>{a.status}</span>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    {["Convocada","Aberta"].includes(a.status) && (<>
                      <button className="btn btn-outline-success btn-sm"
                        disabled={
                          !activeCondo?.idUnidade ||
                          a.minhaPresenca ||
                          confirmandoPresencaId === a.id
                        }
                        onClick={async()=>{
                          if (!activeCondo?.idUnidade) {
                            notify("Selecione sua unidade.", "warning");
                            return;
                          }
                          setConfirmandoPresencaId(a.id);
                          try {
                            await portalService.confirmarPresenca(a.id, activeCondo.idUnidade);
                            notify("Presença confirmada!", "success");
                            const r = await portalService.assembleias(
                              activeCondo.idCondominio,
                              activeCondo.idUnidade,
                            );
                            setAssembleias(r.data || []);
                          } catch(e){ notify(e.message || "Erro ao confirmar presença.", "danger"); }
                          finally { setConfirmandoPresencaId(null); }
                        }}>
                        {confirmandoPresencaId === a.id ? (
                          <><span className="spinner-border spinner-border-sm me-1" role="status"/>
                            Confirmando...</>
                        ) : a.minhaPresenca ? (
                          <><i className="bi bi-check-circle-fill me-1"/>Presença confirmada</>
                        ) : (
                          <><i className="bi bi-person-check-fill me-1"/>Confirmar Presença</>
                        )}
                      </button>
                    </>)}
                    {["Convocada","Aberta","EmAndamento"].includes(a.status) && (<>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={
                          !activeCondo?.idUnidade ||
                          a.minhaVotacaoCompleta ||
                          (a.pautasVotoTotal === 0)
                        }
                        onClick={()=>abrirVotacao(a)}>
                        <i className="bi bi-check2-square me-1"/>
                        {a.minhaVotacaoCompleta ? "Votação registrada" : "Votar"}
                      </button>
                    </>)}
                    {["Encerrada","Realizada"].includes(a.status) && (<>
                      <button className="btn btn-outline-primary btn-sm"
                        onClick={async()=>{
                          try { const r=await portalService.assembleiaResultados(a.id); setResultados(r); setShowResultados(true); }
                          catch(e){ notify(e.message || "Erro ao carregar resultados.", "danger"); }
                        }}>
                        <i className="bi bi-bar-chart-fill me-1"/>Resultados
                      </button>
                      <button className="btn btn-outline-secondary btn-sm"
                        onClick={async()=>{
                          try {
                            const r = await portalService.verAta(a.id);
                            if (!r.ataHtml) { notify("Ata ainda não foi gerada pelo administrador.", "warning"); return; }
                            const blob = new Blob([r.ataHtml], {type:"text/html;charset=utf-8"});
                            const url  = URL.createObjectURL(blob);
                            const w    = window.open(url, "_blank", "width=900,height=950");
                            if (w) { w.onload = () => setTimeout(()=>w.print(), 500); }
                            else {
                              const lk = document.createElement("a");
                              lk.href = url; lk.download = `ata-assembleia-${a.id}.html`;
                              document.body.appendChild(lk); lk.click(); document.body.removeChild(lk);
                            }
                            setTimeout(()=>URL.revokeObjectURL(url), 30000);
                          } catch(e){ notify(e?.message||"Ata não disponível.", "danger"); }
                        }}>
                        <i className="bi bi-file-text me-1"/>Ver Ata
                      </button>
                    </>)}
                    {/* Anexos da assembleia */}
                    {(a.anexos||[]).length > 0 && (
                      <div style={{marginTop:10,borderTop:"1px solid #F1F5F9",paddingTop:10}}>
                        <div style={{fontSize:11,fontWeight:700,color:"var(--gray-400)",marginBottom:6,textTransform:"uppercase"}}>
                          Documentos
                        </div>
                        {(a.anexos||[]).map((ax,i)=>(
                          <button key={i} className="btn btn-outline-secondary btn-sm me-1 mb-1" style={{fontSize:11}}
                            onClick={()=>{
                              const b64=ax.base64?.startsWith("data:")?ax.base64:`data:${ax.tpArquivo||"application/octet-stream"};base64,${ax.base64}`;
                              const lk=document.createElement("a"); lk.href=b64; lk.download=ax.nmArquivo||"documento";
                              document.body.appendChild(lk); lk.click(); document.body.removeChild(lk);
                            }}>
                            <i className="bi bi-file-earmark-arrow-down me-1"/>
                            {ax.nmArquivo}
                            <span style={{fontSize:9,marginLeft:4,opacity:.7}}>{ax.tpAnexo}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── ENCOMENDAS ── */}
        {aba==="encomendas" && (
          <div>
            <h3 style={{fontSize:16,fontWeight:700,marginBottom:14}}>Minhas Encomendas</h3>
            {encomendas.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                <i className="bi bi-box-seam" style={{fontSize:36,display:"block",marginBottom:8}}/>
                Nenhuma encomenda nos últimos 60 dias
              </div>
            ) : encomendas.map(e=>(
              <div key={e.id} className="pcp-card" style={{padding:14,marginBottom:8}}>
                <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
                  {e.fotoEncomenda && (
                    <img src={e.fotoEncomenda} alt="encomenda"
                      style={{width:64,height:64,borderRadius:8,objectFit:"cover",flexShrink:0,
                        cursor:"pointer",border:"2px solid #E2E8F0"}}
                      onClick={()=>window.open(e.fotoEncomenda,"_blank")}/>
                  )}
                  <div style={{flex:1}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                      <div style={{fontWeight:700,fontSize:14}}>{e.descricao}</div>
                      <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:10,whiteSpace:"nowrap",
                        background:e.status==="Aguardando"?"#FEF9C3":"#D1FAE5",
                        color:e.status==="Aguardando"?"#713F12":"#065F46"}}>
                        {e.status==="Aguardando"?"⏳ Aguardando Retirada":"✅ Retirada"}
                      </span>
                    </div>
                    <div style={{fontSize:12,color:"var(--gray-400)",marginTop:4}}>
                      Chegou em {fmtDT(e.dtChegada)}
                      {e.remetente && ` · De: ${e.remetente}`}
                      {e.transportadora && ` · ${e.transportadora}`}
                    </div>
                    {e.status==="Retirada" && e.nmRetiradoPor && (
                      <div style={{fontSize:12,color:"#16A34A",marginTop:4}}>
                        Retirada por: <strong>{e.nmRetiradoPor}</strong>
                        {e.dtRetirada && ` em ${fmtDT(e.dtRetirada)}`}
                      </div>
                    )}
                    {e.status==="Aguardando" && (
                      <div style={{fontSize:12,color:"#B45309",marginTop:6,fontWeight:600}}>
                        📍 Retire na portaria do condomínio
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* COMUNICADOS */}
        {aba==="comunicados" && (
          <div>
            <h3 style={{fontSize:16,fontWeight:700,marginBottom:14}}>Comunicados</h3>
            {comunicados.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                <i className="bi bi-megaphone" style={{fontSize:36,display:"block",marginBottom:8}}/>Nenhum comunicado
              </div>
            ) : comunicados.map(c=>(
              <div key={c.id} className="pcp-card"
                style={{padding:14,marginBottom:8,borderLeft:`4px solid ${c.lido?"#E2E8F0":"#1B3A6B"}`}}>
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"flex-start",gap:8,flexWrap:"wrap"}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:14,color:c.lido?"#64748B":"#1E293B"}}>
                      {!c.lido&&<span style={{background:"#EF4444",color:"#fff",fontSize:10,
                        borderRadius:6,padding:"1px 6px",marginRight:6,fontWeight:700}}>Novo</span>}
                      {c.titulo||c.assunto}
                    </div>
                    <div style={{fontSize:12,color:"var(--gray-400)",marginTop:2}}>
                      {fmtD(c.dtPublicacao)}
                      {c.categoria && <span style={{marginLeft:6,background:"var(--bg-page)",padding:"1px 6px",borderRadius:4,fontSize:11}}>{c.categoria}</span>}
                    </div>
                    {(c.conteudo||c.descricao)&&(
                      <div style={{fontSize:13,color:"#374151",marginTop:8,lineHeight:1.6,
                        background:"var(--bg-surface-alt)",borderRadius:6,padding:"8px 10px"}}>
                        {c.conteudo||c.descricao}
                      </div>
                    )}
                    {(c.anexos||[]).length>0&&(
                      <div style={{marginTop:8}}>
                        {(c.anexos||[]).map((a,i)=>(
                          <button key={i} className="btn btn-outline-secondary btn-sm me-1 mb-1" style={{fontSize:11}}
                            onClick={()=>{
                              const b64=a.base64&&a.base64.startsWith("data:")?a.base64:a.base64?`data:${a.tpArquivo||"application/octet-stream"};base64,${a.base64}`:null;
                              if(!b64){window.open(`/uploads/comunicados/${a.nmArquivo}`,"_blank");return;}
                              const link=document.createElement("a"); link.href=b64; link.download=a.nmArquivo||"anexo";
                              document.body.appendChild(link); link.click(); document.body.removeChild(link);
                            }}>
                            <i className="bi bi-paperclip me-1"/>{a.nmArquivo}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {!c.lido&&(
                    <button className="btn btn-outline-secondary btn-sm" style={{flexShrink:0}}
                      onClick={async()=>{
                        await portalService.confirmarLeitura(c.id, {
                          idUnidade: activeCondo?.idUnidade,
                        });
                        const r = await portalService.comunicados(
                          activeCondo?.idCondominio,
                          activeCondo?.idUnidade,
                        );
                        setComunicados(r.data||[]);
                      }}>
                      Lido
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* OCORRÊNCIAS */}
        {aba==="ocorrencias" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",marginBottom:14}}>
              <h3 style={{margin:0,fontSize:16,fontWeight:700}}>Ocorrências</h3>
              <button className="btn btn-primary btn-sm"
                onClick={()=>{
                  const u=condoAtivo;
                  setFormOco({idCondominio:String(u?.idCondominio||""),
                    idUnidade:String(u?.idUnidade||""),titulo:"",descricao:"",tpOcorrencia:"Reclamacao",prioridade:"Media"});
                  setOcoAnexosFiles([]);
                  setShowOcoModal(true);
                }}>
                <i className="bi bi-plus-lg me-1"/>Nova
              </button>
            </div>
            {ocorrencias.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                <i className="bi bi-exclamation-circle" style={{fontSize:36,display:"block",marginBottom:8}}/>Nenhuma ocorrência
              </div>
            ) : ocorrencias.map(o=>{
              const sBg={Aberta:"#FEF9C3",EmAndamento:"#DBEAFE",AguardandoCondomino:"#FEE2E2",Resolvida:"#D1FAE5",Fechada:"#F1F5F9",Cancelada:"#F1F5F9"}[o.status]||"#F1F5F9";
              const sColor={Aberta:"#713F12",EmAndamento:"#1E40AF",AguardandoCondomino:"#991B1B",Resolvida:"#065F46",Fechada:"#475569",Cancelada:"#94A3B8"}[o.status]||"#64748B";
              const expanded = ocoSel===o.id;
              return (
                <div key={o.id} className="pcp-card" style={{padding:14,marginBottom:8,cursor:"pointer"}}
                  onClick={()=>setOcoSel(expanded?null:o.id)}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:14}}>{o.titulo}</div>
                      <div style={{fontSize:12,color:"var(--gray-400)",marginTop:2}}>
                        {fmtD(o.dtAbertura||o.createdAt)} · {o.tpOcorrencia}
                        {o.prioridade && ` · ${o.prioridade}`}
                        {o.dtPrazo && ` · Prazo ${fmtD(o.dtPrazo)}`}
                        {o.nmUnidade&&` · Ap.${o.nmUnidade}${o.bloco?`/Bl.${o.bloco}`:""}`}
                      </div>
                    </div>
                    <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:10,
                      background:sBg,color:sColor,whiteSpace:"nowrap",flexShrink:0}}>
                      {o.status==="AguardandoCondomino"?"⚠️ Aguardando você":o.status}
                    </span>
                  </div>
                  {expanded&&(
                    <div style={{marginTop:12,borderTop:"1px solid #F1F5F9",paddingTop:12}}
                      onClick={e=>e.stopPropagation()}>
                      <p style={{fontSize:13,color:"var(--gray-600)",marginBottom:12}}>{o.descricao}</p>
                      {(o.timeline||[]).length>0&&(
                        <div style={{marginBottom:12}}>
                          <div style={{fontSize:11,fontWeight:700,color:"var(--gray-400)",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.5px"}}>Histórico</div>
                          {(o.timeline||[]).map((t,i)=>(
                            <div key={i} style={{fontSize:12,padding:"6px 10px",background:"var(--bg-surface-alt)",borderRadius:6,marginBottom:4,borderLeft:"3px solid #CBD5E1"}}>
                              <span style={{color:"var(--gray-400)",marginRight:6}}>{fmtD(t.createdAt)}</span>
                              <span style={{color:"var(--gray-600)"}}>{t.descricao}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {(o.anexos||[]).length>0&&(
                        <div style={{marginBottom:12}}>
                          <div style={{fontSize:11,fontWeight:700,color:"var(--gray-400)",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.5px"}}>Anexos</div>
                          {(o.anexos||[]).map((a,i)=>(
                            <button key={a.id||i} className="btn btn-outline-secondary btn-sm me-1 mb-1" style={{fontSize:12}}
                              onClick={async()=>{
                                const mime=a.tpArquivo||"application/octet-stream";
                                let raw=a.base64;
                                if(!raw&&a.id){
                                  try{
                                    const r=await portalService.downloadOcorrenciaAnexo(o.id,a.id);
                                    raw=r?.data?.base64;
                                  }catch(err){ notify(err.message||"Erro ao baixar anexo.","danger"); return; }
                                }
                                const b64=raw&&String(raw).startsWith("data:")?raw:raw?`data:${mime};base64,${raw}`:null;
                                if(!b64){ notify("Anexo indisponível.","warning"); return; }
                                const lk=document.createElement("a");lk.href=b64;lk.download=a.nmArquivo||"anexo";
                                document.body.appendChild(lk);lk.click();document.body.removeChild(lk);
                              }}>
                              <i className="bi bi-paperclip me-1"/>{a.nmArquivo}
                            </button>
                          ))}
                        </div>
                      )}
                      {o.status==="AguardandoCondomino"&&(
                        <div style={{background:"var(--bg-warning-soft)",borderRadius:8,padding:12,marginTop:8,border:"1px solid #FDE047"}}>
                          <div style={{fontSize:12,fontWeight:700,color:"#713F12",marginBottom:8}}>⚠️ A administração aguarda sua resposta</div>
                          <textarea className="form-control" rows={3} style={{fontSize:13}}
                            placeholder="Digite sua resposta..."
                            value={respostaOco}
                            onChange={e=>setRespostaOco(e.target.value)}/>
                          <button className="btn btn-primary btn-sm mt-2"
                            onClick={async()=>{
                              if(!respostaOco.trim())return;
                              try{
                                await portalService.responderOcorrencia(o.id,respostaOco);
                                setRespostaOco("");
                                const r=await portalService.ocorrencias(activeCondo?.idCondominio, activeCondo?.idUnidade);
                                setOcorrencias(r.data||[]);
                              }catch(err){notify(err.message || "Erro ao enviar resposta.", "danger");}
                            }}>
                            <i className="bi bi-send me-1"/>Enviar Resposta
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* PRESTAÇÃO */}
        {aba==="prestacao" && (
          <div>
            <h3 style={{fontSize:16,fontWeight:700,marginBottom:14}}>Prestação de Contas</h3>
            {prestacoes.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                <i className="bi bi-file-earmark-bar-graph" style={{fontSize:36,display:"block",marginBottom:8}}/>
                Nenhuma prestação publicada
              </div>
            ) : prestacoes.map(p=>(
              <div key={p.id} className="pcp-card" style={{padding:14,marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"center",flexWrap:"wrap",gap:10}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:14}}>{p.titulo||"Prestação de Contas"}</div>
                    <div style={{fontSize:12,color:"var(--gray-400)",marginTop:2}}>
                      {fmtD(p.dtIni)} até {fmtD(p.dtFim)} · Publicado {fmtD(p.dtPublicacao)}
                    </div>
                  </div>
                  <button
                    style={{background:"#1B3A6B",color:"#fff",border:"none",padding:"8px 16px",borderRadius:8,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}
                    onClick={async()=>{
                      if(p.tpItem==="prestacao-fechamento") {
                        try {
                          const r = await portalService.prestacaoFechamento(p.idCondominio, p.competencia);
                          if(r?.html) {
                            const w=window.open("","_blank","width=950,height=900");
                            if(w){ w.document.write(r.html); w.document.close(); }
                          } else { notify("Relatório não disponível.", "warning"); }
                        } catch(e){ notify(e.message||"Erro ao abrir.", "danger"); }
                      } else { abrirPrestacao(p.id); }
                    }}>
                    <i className="bi bi-eye me-1"/>Visualizar
                  </button>
                </div>
              </div>
            ))}
            {loadingPrest&&<div style={{textAlign:"center",padding:20}}><span className="spinner-border text-primary"/></div>}
            {prestacaoAtiva&&!loadingPrest&&(
              <div style={{marginTop:16,border:"1px solid var(--border-color)",borderRadius:10,overflow:"hidden"}}>
                <div style={{background:"var(--bg-surface-alt)",padding:"10px 14px",
                  display:"flex",justifyContent:"space-between",alignItems:"center",
                  borderBottom:"1px solid var(--border-color)",flexWrap:"wrap",gap:8}}>
                  <span style={{fontWeight:700,fontSize:13}}>{prestacaoAtiva.titulo}</span>
                  <div style={{display:"flex",gap:8}}>
                    <button className="btn btn-primary btn-sm"
                      onClick={()=>imprimirPrestacao(prestacaoAtiva.html||prestacaoAtiva.htmlRelatorio||"")}>
                      <i className="bi bi-printer me-1"/>PDF
                    </button>
                    <button className="btn btn-outline-secondary btn-sm"
                      onClick={()=>setPrestacaoAtiva(null)}>
                      <i className="bi bi-x"/>
                    </button>
                  </div>
                </div>
                <div style={{padding:16,background:"var(--bg-surface)",overflowX:"auto"}}
                  dangerouslySetInnerHTML={{__html:prestacaoAtiva.html||prestacaoAtiva.htmlRelatorio||""}}/>
              </div>
            )}
          </div>
        )}

        {/* DEMONSTRATIVOS FINANCEIROS */}
        {aba==="demonstrativos" && (
          <div>
            {viewDemonstrativo ? (
              <div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <span style={{fontWeight:700,fontSize:15}}>📊 Demonstrativo {viewDemonstrativo.competencia}</span>
                  <button className="btn btn-outline-secondary btn-sm" onClick={()=>setViewDemonstrativo(null)}>
                    <i className="bi bi-arrow-left me-1"/>Voltar
                  </button>
                </div>
                <iframe
                  srcDoc={viewDemonstrativo.html}
                  style={{width:"100%",height:"80vh",border:"1px solid var(--border-color)",borderRadius:8}}
                  title="Demonstrativo"
                />
              </div>
            ) : (
              <div>
                <h6 style={{fontWeight:700,marginBottom:16}}>📊 Demonstrativos Financeiros</h6>
                {demonstrativos.length === 0 ? (
                  <div style={{textAlign:"center",padding:32,color:"var(--gray-400)"}}>
                    <i className="bi bi-bar-chart-line" style={{fontSize:36}}/>
                    <p style={{marginTop:8}}>Nenhum demonstrativo disponível ainda</p>
                  </div>
                ) : (
                  <div className="row g-3">
                    {demonstrativos.map(dem=>(
                      <div key={dem.id} className="col-12">
                        <div style={{background:"var(--bg-surface)",borderRadius:12,padding:"14px 18px",boxShadow:"0 1px 4px rgba(0,0,0,.08)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <div>
                            <div style={{fontWeight:700,fontSize:14}}>
                              {(()=>{const [ano,mes]=(dem.competencia||"").split("-");const meses=["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];return `Demonstrativo Receita e Despesa ${meses[parseInt(mes)-1]||mes}/${ano}`;})()}
                            </div>
                            <div style={{fontSize:12,color:"var(--gray-500)",marginTop:2}}>
                              Fechado em {new Date(dem.dtFechamento).toLocaleDateString("pt-BR")}
                            </div>
                          </div>
                          {true ? (
                            <button className="btn btn-primary btn-sm" onClick={async()=>{
                              try {
                                const r = await portalService.demonstrativo(dem.id);
                                if(r.html) setViewDemonstrativo({html:r.html, competencia:dem.competencia});
                              } catch(e){ notify(e.message || "Erro ao carregar demonstrativo.", "danger"); }
                            }}>
                              <i className="bi bi-eye me-1"/>Visualizar
                            </button>
                          ) : (
                            <span style={{fontSize:12,color:"var(--gray-400)"}}>PDF não disponível</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}


        {/* AUTORIZAÇÕES */}
        {aba==="autorizacoes" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",marginBottom:14}}>
              <h3 style={{margin:0,fontSize:16,fontWeight:700}}>Autorizações</h3>
              <button className="btn btn-primary btn-sm"
                onClick={()=>{setFormAuth(E_AUTH);setShowNovaAuth(true);}}>
                <i className="bi bi-plus-lg me-1"/>Nova
              </button>
            </div>
            {autorizacoes.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                <i className="bi bi-qr-code" style={{fontSize:36,display:"block",marginBottom:8}}/>
                <p style={{margin:0}}>Nenhuma autorização ativa</p>
                <p style={{fontSize:12,marginTop:4}}>Autorize visitantes antes da chegada</p>
              </div>
            ) : autorizacoes.map(a=>(
              <div key={a.id} className="pcp-card" style={{padding:14,marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",
                  flexWrap:"wrap",gap:8}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:14}}>{a.nmVisitante}</div>
                    <div style={{fontSize:12,color:"var(--gray-400)"}}>
                      {a.tpVisita} · Válido até {fmtD(a.dtValidade)}
                      {a.hrInicio&&` · ${a.hrInicio}–${a.hrFim||"?"}`}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button className="btn btn-outline-secondary btn-sm"
                      onClick={()=>setQrVisible(q=>({...q,[a.id]:!q[a.id]}))}>
                      <i className="bi bi-qr-code"/>
                    </button>
                    <button className="btn btn-outline-danger btn-sm"
                      onClick={async()=>{
                        const ok = await askConfirm({ titulo:"Cancelar autorização?", mensagem:`Cancelar a autorização de ${a.nmVisitante}?`, textoOk:"Cancelar", perigo:true });
                        if(!ok) return;
                        await portariaService.cancelarAutorizacao(a.id).catch(()=>{});
                        const r=await portariaService.preautorizacoes({idCondominio:condoAtivo?.idCondominio});
                        setAutorizacoes(r.data||[]);
                        notify("Autorização cancelada.", "success");
                      }}>
                      <i className="bi bi-x"/>
                    </button>
                  </div>
                </div>
                {qrVisible[a.id]&&(
                  <div style={{marginTop:10,textAlign:"center",background:"var(--bg-surface-alt)",borderRadius:10,padding:16}}>
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${a.qrToken}&bgcolor=ffffff&color=1B3A6B`}
                      alt="QR Code"
                      style={{width:200,height:200,borderRadius:8,border:"3px solid #1B3A6B"}}
                    />
                    <div style={{fontSize:11,color:"var(--gray-400)",marginTop:8}}>
                      Válido até {fmtD(a.dtValidade)}{a.hrInicio?` · ${a.hrInicio}–${a.hrFim||"?"}` : ""}
                    </div>
                    <button className="btn btn-outline-primary btn-sm mt-2"
                      onClick={()=>{
                        const url = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${a.qrToken}&bgcolor=ffffff&color=1B3A6B`;
                        const w = window.open("","_blank","width=500,height=580");
                        w.document.write(`<html><body style="margin:20px;text-align:center;font-family:sans-serif">
                          <h3 style="color:#1B3A6B">Autorização de Acesso</h3>
                          <p><strong>${a.nmVisitante}</strong> · ${a.tpVisita}</p>
                          <img src="${url}" style="width:320px;height:320px;border:4px solid #1B3A6B;border-radius:10px"/><br/>
                          <p style="color:#64748B;font-size:13px">Válido até ${fmtD(a.dtValidade)}${a.hrInicio?` · ${a.hrInicio}–${a.hrFim||"?"}`:""}</p>
                          <button onclick="window.print()" style="padding:8px 20px;background:#1B3A6B;color:white;border:none;border-radius:6px;cursor:pointer;font-size:14px">🖨️ Imprimir</button>
                        </body></html>`);
                        w.document.close();
                      }}>
                      <i className="bi bi-printer me-1"/>Imprimir / Ampliar
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* MORADORES */}
        {aba==="moradores" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",marginBottom:14}}>
              <h3 style={{margin:0,fontSize:16,fontWeight:700}}>Moradores</h3>
              <button className="btn btn-primary btn-sm"
                onClick={()=>{setFormMor(E_MOR);setEditMorador(null);setShowNovoMor(true);}}>
                <i className="bi bi-person-plus me-1"/>Adicionar
              </button>
            </div>
            {moradores.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                <i className="bi bi-people" style={{fontSize:36,display:"block",marginBottom:8}}/>Nenhum morador
              </div>
            ) : moradores.map(m=>(
              <div key={m.id} className="pcp-card"
                style={{padding:14,marginBottom:8,display:"flex",
                  justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:14}}>{m.nmMorador}</div>
                  <div style={{fontSize:12,color:"var(--gray-400)"}}>
                    {m.tpMorador}{m.parentesco?` — ${m.parentesco}`:""}
                    {m.telefone?` · ${m.telefone}`:""}
                  </div>
                </div>
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  <button className="btn btn-outline-secondary btn-sm"
                    onClick={()=>{setEditMorador(m);setFormMor({...m});setShowNovoMor(true);}}>
                    <i className="bi bi-pencil"/>
                  </button>
                  <button className="btn btn-outline-danger btn-sm"
                    onClick={async()=>{
                      const ok = await askConfirm({ titulo:"Remover morador?", mensagem:`Remover ${m.nmMorador}?`, textoOk:"Remover", perigo:true });
                      if(!ok) return;
                      await portalService.removerMorador(m.id).catch(()=>{});
                      const r=await portalService.moradores(activeCondo?.idCondominio, activeCondo?.idUnidade);
                      setMoradores(r.data||[]);
                      notify("Morador removido.", "success");
                    }}>
                    <i className="bi bi-trash3"/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* HISTÓRICO */}
        {aba==="historico" && (
          <div>
            <h3 style={{fontSize:16,fontWeight:700,marginBottom:14}}>Histórico de Visitas</h3>
            {historico.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                <i className="bi bi-clock-history" style={{fontSize:36,display:"block",marginBottom:8}}/>Nenhuma visita
              </div>
            ) : historico.map(r=>(
              <div key={r.id} className="pcp-card"
                style={{padding:14,marginBottom:8,
                  borderLeft:`4px solid ${r.dtSaida?"#E2E8F0":"#166534"}`}}>
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"center",flexWrap:"wrap",gap:8}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:14}}>{r.nmVisitante}</div>
                    <div style={{fontSize:12,color:"var(--gray-400)"}}>
                      {r.tpVisita}{r.empresa?` · ${r.empresa}`:""}
                      · {fmtDT(r.dtEntrada)}
                      {r.dtSaida&&` → ${fmtDT(r.dtSaida)}`}
                    </div>
                  </div>
                  {!r.dtSaida&&(
                    <span style={{fontSize:11,fontWeight:700,padding:"3px 8px",
                      borderRadius:10,background:"var(--bg-success-soft)",color:"#166534",flexShrink:0}}>
                      ● Dentro
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* VEÍCULOS */}
        {aba==="veiculos" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <h3 style={{margin:0,fontSize:16,fontWeight:700}}>Meus Veículos</h3>
              <button className="btn btn-primary btn-sm"
                onClick={()=>{setFormVei({idUnidade:String(activeCondo?.idUnidade||""),placa:"",modelo:"",cor:"",tpVeiculo:"Carro",foto:null});setEditVeiculo(null);setShowNovoVei(true);}}>
                <i className="bi bi-plus-lg me-1"/>Adicionar
              </button>
            </div>
            {veiculos.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                <i className="bi bi-car-front" style={{fontSize:36,display:"block",marginBottom:8}}/>
                Nenhum veículo cadastrado
              </div>
            ) : veiculos.map(v=>(
              <div key={v.id} className="pcp-card"
                style={{padding:14,marginBottom:8,display:"flex",
                  justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontFamily:"monospace",fontSize:14}}>{v.placa}</div>
                  <div style={{fontSize:12,color:"var(--gray-400)"}}>
                    {v.tpVeiculo}{v.modelo?` · ${v.modelo}`:""}{v.cor?` · ${v.cor}`:""}
                  </div>
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button className="btn btn-outline-secondary btn-sm"
                    onClick={()=>{setEditVeiculo(v);setFormVei({...v});setShowNovoVei(true);}}>
                    <i className="bi bi-pencil"/>
                  </button>
                  <button className="btn btn-outline-danger btn-sm"
                    onClick={async()=>{
                      const ok = await askConfirm({ titulo:"Remover veículo?", mensagem:`Remover o veículo ${v.placa}?`, textoOk:"Remover", perigo:true });
                      if(!ok) return;
                      await portalService.removerVeiculo(v.id).catch(()=>{});
                      const r=await portalService.veiculos(activeCondo?.idCondominio, activeCondo?.idUnidade);
                      setVeiculos(r.data||[]);
                      notify("Veículo removido.", "success");
                    }}>
                    <i className="bi bi-trash3"/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* PORTARIA — avisos ao porteiro */}
        {aba==="portaria" && (
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, marginBottom: 14 }}>
              Aviso à portaria
            </h3>
            <p style={{ fontSize: 13, color:"var(--gray-500)", marginBottom: 16 }}>
              Envie um aviso ao porteiro sobre visitantes, entregas ou prestadores. Enquanto não estiver resolvido,
              você e a portaria podem trocar mensagens no mesmo aviso.
            </p>
            <form className="pcp-card" style={{ padding: 16, marginBottom: 20 }}
              onSubmit={async (e) => {
                e.preventDefault();
                if (!formAvisoPort.mensagem.trim()) {
                  notify("Digite a mensagem para a portaria.", "warning");
                  return;
                }
                setEnviandoAviso(true);
                try {
                  await portalService.criarAvisoPortaria({
                    idCondominio: activeCondo?.idCondominio,
                    idUnidade: activeCondo?.idUnidade,
                    tpAviso: formAvisoPort.tpAviso,
                    urgencia: formAvisoPort.urgencia,
                    mensagem: formAvisoPort.mensagem.trim(),
                  });
                  notify("Aviso enviado à portaria.", "success");
                  setFormAvisoPort({ tpAviso: "Outro", urgencia: "Normal", mensagem: "" });
                  const r = await portalService.avisosPortaria(
                    activeCondo?.idCondominio, activeCondo?.idUnidade
                  );
                  setAvisosPortaria(r.data || []);
                } catch (err) {
                  notify(err.message || "Erro ao enviar aviso.", "danger");
                }
                setEnviandoAviso(false);
              }}>
              <div className="row g-2 mb-2">
                <div className="col-6">
                  <label className="form-label" style={{ fontSize: 12 }}>Tipo</label>
                  <select className="form-select form-select-sm" value={formAvisoPort.tpAviso}
                    onChange={(e) => setFormAvisoPort((f) => ({ ...f, tpAviso: e.target.value }))}>
                    {["Visitante", "Entrega", "Prestador", "Outro"].map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label" style={{ fontSize: 12 }}>Urgência</label>
                  <select className="form-select form-select-sm" value={formAvisoPort.urgencia}
                    onChange={(e) => setFormAvisoPort((f) => ({ ...f, urgencia: e.target.value }))}>
                    <option value="Normal">Normal</option>
                    <option value="Urgente">Urgente</option>
                  </select>
                </div>
              </div>
              <label className="form-label" style={{ fontSize: 12 }}>Mensagem</label>
              <textarea className="form-control form-control-sm mb-3" rows={3} required
                placeholder="Ex.: Visitante João chega às 15h; aguardar na portaria."
                value={formAvisoPort.mensagem}
                onChange={(e) => setFormAvisoPort((f) => ({ ...f, mensagem: e.target.value }))}/>
              <button type="submit" className="btn btn-primary btn-sm" disabled={enviandoAviso}>
                {enviandoAviso ? "Enviando…" : <><i className="bi bi-send me-1"/>Enviar à portaria</>}
              </button>
            </form>
            <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Histórico</h4>
            <AbasAvisos
              aba={abaListaPortaria}
              onAba={setAbaListaPortaria}
              qtdAbertos={contarAvisos(avisosPortaria).abertos}
              qtdResolvidos={contarAvisos(avisosPortaria).resolvidos}
            />
            {avisosPortaria.length === 0 ? (
              <div style={{ textAlign: "center", padding: 24, color:"var(--gray-400)", fontSize: 13 }}>
                Nenhum aviso enviado ainda.
              </div>
            ) : filtrarAvisos(avisosPortaria, { aba: abaListaPortaria }).length === 0 ? (
              <div style={{ textAlign: "center", padding: 24, color:"var(--gray-400)", fontSize: 13 }}>
                {abaListaPortaria === "abertos"
                  ? "Nenhum aviso em aberto."
                  : "Nenhum aviso resolvido."}
              </div>
            ) : (
              <div>
                {filtrarAvisos(avisosPortaria, { aba: abaListaPortaria }).map((a) => (
                  <AvisoCardColapsavel
                    key={a.id}
                    aviso={a}
                    aberto={!!expandidosPortaria[a.id]}
                    onToggle={() => setExpandidosPortaria((e) => ({ ...e, [a.id]: !e[a.id] }))}
                    labelUnidade={`${a.tpAviso}${a.urgencia === "Urgente" ? " · Urgente" : ""}`}
                    cardClass="pcp-card"
                  >
                    <HistoricoAviso historico={a.historico} labelCondomino="Você" />
                    {a.status !== "Resolvido" && (
                      <div style={{ marginTop: 8 }}>
                        <textarea className="form-control form-control-sm mb-2" rows={2}
                          placeholder="Continuar conversa com a portaria…"
                          value={msgAvisoAberto[a.id] || ""}
                          onChange={(e) => setMsgAvisoAberto((s) => ({ ...s, [a.id]: e.target.value }))}/>
                        <button type="button" className="btn btn-outline-primary btn-sm"
                          disabled={!!enviandoMsgAviso[a.id]}
                          onClick={async () => {
                            const txt = (msgAvisoAberto[a.id] || "").trim();
                            if (!txt) { notify("Digite uma mensagem.", "warning"); return; }
                            setEnviandoMsgAviso((s) => ({ ...s, [a.id]: true }));
                            try {
                              await portalService.responderAvisoPortaria(a.id, txt);
                              notify("Mensagem enviada à portaria.", "success");
                              setMsgAvisoAberto((s) => { const n = { ...s }; delete n[a.id]; return n; });
                              const r = await portalService.avisosPortaria(
                                activeCondo?.idCondominio, activeCondo?.idUnidade
                              );
                              setAvisosPortaria(r.data || []);
                            } catch (err) {
                              notify(err.message || "Erro ao enviar.", "danger");
                            }
                            setEnviandoMsgAviso((s) => ({ ...s, [a.id]: false }));
                          }}>
                          {enviandoMsgAviso[a.id] ? "Enviando…" : "Enviar mensagem"}
                        </button>
                      </div>
                    )}
                  </AvisoCardColapsavel>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PETS */}
        {aba==="pets" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <h3 style={{margin:0,fontSize:16,fontWeight:700}}>Meus Pets</h3>
              <button className="btn btn-primary btn-sm"
                onClick={()=>{setFormPet({idUnidade:String(activeCondo?.idUnidade||""),nmPet:"",especie:"Cachorro",raca:"",cor:"",foto:null,observacao:""});setEditPet(null);setShowNovoPet(true);}}>
                <i className="bi bi-plus-lg me-1"/>Adicionar
              </button>
            </div>
            {pets.length===0 ? (
              <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                <i className="bi bi-heart" style={{fontSize:36,display:"block",marginBottom:8}}/>
                Nenhum pet cadastrado
              </div>
            ) : pets.map(p=>(
              <div key={p.id} className="pcp-card"
                style={{padding:14,marginBottom:8,display:"flex",
                  justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:14}}>
                    {p.especie==="Cachorro"?"🐕 ":p.especie==="Gato"?"🐈 ":p.especie==="Pássaro"?"🐦 ":"🐾 "}
                    {p.nmPet}
                  </div>
                  <div style={{fontSize:12,color:"var(--gray-400)"}}>
                    {p.especie}{p.raca?` · ${p.raca}`:""}{p.cor?` · ${p.cor}`:""}
                  </div>
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button className="btn btn-outline-secondary btn-sm"
                    onClick={()=>{setEditPet(p);setFormPet({...p});setShowNovoPet(true);}}>
                    <i className="bi bi-pencil"/>
                  </button>
                  <button className="btn btn-outline-danger btn-sm"
                    onClick={async()=>{
                      const ok = await askConfirm({ titulo:"Remover pet?", mensagem:`Remover ${p.nmPet}?`, textoOk:"Remover", perigo:true });
                      if(!ok) return;
                      await portalService.removerPet(p.id).catch(()=>{});
                      const r=await portalService.pets(activeCondo?.idCondominio, activeCondo?.idUnidade);
                      setPets(r.data||[]);
                      notify("Pet removido.", "success");
                    }}>
                    <i className="bi bi-trash3"/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        </div>{/* /maxWidth */}
      </div>{/* /conteúdo */}

      {/* ═══ MODAL: OCORRÊNCIA ═══ */}
      {showOcoModal&&(
        <div className="pcp-modal-overlay" onClick={()=>{ setShowOcoModal(false); setOcoAnexosFiles([]); }}>
          <div className="pcp-modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-exclamation-circle me-2"/>Nova Ocorrência</h5>
              <button className="btn-close" onClick={()=>{ setShowOcoModal(false); setOcoAnexosFiles([]); }}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                {condos.length>1&&(
                  <div className="col-12">
                    <label className="form-label">Unidade</label>
                    <select className="form-select" value={formOco.idUnidade}
                      onChange={e=>{const u=condos.find(x=>String(x.idUnidade)===e.target.value);
                        setFormOco(p=>({...p,idUnidade:e.target.value,idCondominio:String(u?.idCondominio||"")}));}}>
                      {condos.map(u=><option key={u.idUnidade} value={u.idUnidade}>
                        {u.nmFantasia||u.nmCondominio} — Un. {u.nmUnidade}
                      </option>)}
                    </select>
                  </div>
                )}
                <div className="col-12 col-lg-7">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={formOco.tpOcorrencia}
                    onChange={e=>setFormOco(p=>({...p,tpOcorrencia:e.target.value}))}>
                    {[{v:"Reclamacao",l:"Reclamação"},{v:"Manutencao",l:"Manutenção"},{v:"Solicitacao",l:"Solicitação"},{v:"Seguranca",l:"Segurança"},{v:"Sugestao",l:"Sugestão"},{v:"Outro",l:"Outro"}].map(t=><option key={t.v} value={t.v}>{t.l}</option>)}
                  </select>
                </div>
                <div className="col-12 col-lg-5">
                  <label className="form-label">Prioridade</label>
                  <div className="pcp-oco-prio-box">
                    <select className="form-select" value={formOco.prioridade||"Media"}
                      onChange={e=>setFormOco(p=>({...p,prioridade:e.target.value}))}>
                      {[{v:"Urgente",l:"Urgente"},{v:"Alta",l:"Alta"},{v:"Media",l:"Média"},{v:"Baixa",l:"Baixa"}].map(p=><option key={p.v} value={p.v}>{p.l}</option>)}
                    </select>
                    <div className="pcp-oco-prio-hint">
                      {OCO_PRIOR_HINT[formOco.prioridade||"Media"] || OCO_PRIOR_HINT.Media}
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label">Título *</label>
                  <input className="form-control" value={formOco.titulo}
                    onChange={e=>setFormOco(p=>({...p,titulo:e.target.value}))}/>
                </div>
                <div className="col-12">
                  <label className="form-label">Descrição *</label>
                  <textarea className="form-control" rows={3} value={formOco.descricao}
                    onChange={e=>setFormOco(p=>({...p,descricao:e.target.value}))}/>
                </div>
                <div className="col-12">
                  <label className="form-label">Anexos <span className="text-muted fw-normal" style={{fontSize:12}}>(opcional)</span></label>
                  <input type="file" className="form-control form-control-sm" multiple accept={OCO_ANEXO_ACCEPT}
                    onChange={onOcoAnexosChange}/>
                  <div className="form-text">Até {OCO_ANEXO_MAX} arquivos, até 2 MB cada (JPG, PNG, GIF, WEBP ou PDF).</div>
                  {ocoAnexosFiles.length > 0 && (
                    <ul className="list-unstyled small mt-2 mb-0">
                      {ocoAnexosFiles.map((f, idx)=>(
                        <li key={`${f.name}-${idx}`} className="d-flex align-items-center justify-content-between py-1 border-bottom border-light">
                          <span className="text-truncate me-2" title={f.name}><i className="bi bi-paperclip me-1"/>{f.name}</span>
                          <button type="button" className="btn btn-link btn-sm text-danger p-0 flex-shrink-0"
                            onClick={()=>setOcoAnexosFiles(prev=>prev.filter((_,i)=>i!==idx))}>
                            Remover
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>{ setShowOcoModal(false); setOcoAnexosFiles([]); }}>Cancelar</button>
              <button className="btn btn-primary" disabled={saving}
                onClick={async()=>{
                  if(!formOco.titulo?.trim()){ notify("Informe o título.", "warning"); return; }
                  if(!formOco.descricao.trim()){ notify("Informe a descrição.", "warning"); return; }
                  setSaving(true);
                  try{
                    let anexos = [];
                    if (ocoAnexosFiles.length) {
                      anexos = await Promise.all(ocoAnexosFiles.map(f => new Promise((resolve, reject) => {
                        const fr = new FileReader();
                        fr.onload = () => resolve({
                          nmArquivo: f.name,
                          mimeType: f.type || "application/octet-stream",
                          tamanho: f.size,
                          base64: fr.result,
                        });
                        fr.onerror = () => reject(new Error("Falha ao ler um dos anexos."));
                        fr.readAsDataURL(f);
                      })));
                    }
                    await portalService.criarOcorrencia({ ...formOco, anexos });
                    setShowOcoModal(false);
                    setOcoAnexosFiles([]);
                    const r=await portalService.ocorrencias(activeCondo?.idCondominio, activeCondo?.idUnidade);
                    setOcorrencias(r.data||[]);
                    notify("Ocorrência registrada com sucesso!", "success");
                  }
                  catch(e){ notify(e.message || "Erro ao registrar ocorrência.", "danger"); }
                  setSaving(false);
                }}>
                {saving?"Enviando...":"Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL: AUTORIZAÇÃO ═══ */}
      {showNovaAuth&&(
        <div className="pcp-modal-overlay" onClick={()=>setShowNovaAuth(false)}>
          <div className="pcp-modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-qr-code me-2"/>Nova Autorização</h5>
              <button className="btn-close" onClick={()=>setShowNovaAuth(false)}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-8">
                  <label className="form-label">Visitante *</label>
                  <input className="form-control" value={formAuth.nmVisitante} autoFocus
                    onChange={e=>setFormAuth(p=>({...p,nmVisitante:e.target.value}))}/>
                </div>
                <div className="col-4">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={formAuth.tpVisita}
                    onChange={e=>setFormAuth(p=>({...p,tpVisita:e.target.value}))}>
                    {["Visitante","Prestador","Familiar","Entrega","Outro"].map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label">Válido até *</label>
                  <input type="date" className="form-control" value={formAuth.dtValidade}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={e=>setFormAuth(p=>({...p,dtValidade:e.target.value}))}/>
                </div>
                <div className="col-3">
                  <label className="form-label">Das</label>
                  <input type="time" className="form-control" value={formAuth.hrInicio}
                    onChange={e=>setFormAuth(p=>({...p,hrInicio:e.target.value}))}/>
                </div>
                <div className="col-3">
                  <label className="form-label">Até</label>
                  <input type="time" className="form-control" value={formAuth.hrFim}
                    onChange={e=>setFormAuth(p=>({...p,hrFim:e.target.value}))}/>
                </div>
                <div className="col-6">
                  <label className="form-label">Documento</label>
                  <input className="form-control" value={formAuth.documento}
                    onChange={e=>setFormAuth(p=>({...p,documento:e.target.value}))} placeholder="RG ou CPF"/>
                </div>
                <div className="col-6">
                  <label className="form-label">E-mail do Visitante <small style={{color:"var(--gray-400)"}}>(opcional — envia QR)</small></label>
                  <input type="email" className="form-control" value={formAuth.emailVisitante}
                    onChange={e=>setFormAuth(p=>({...p,emailVisitante:e.target.value}))}
                    placeholder="email@exemplo.com"/>
                </div>
                <div className="col-12">
                  <label className="form-label">Observação</label>
                  <input className="form-control" value={formAuth.observacao}
                    onChange={e=>setFormAuth(p=>({...p,observacao:e.target.value}))}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowNovaAuth(false)}>Cancelar</button>
              <button className="btn btn-primary" disabled={saving}
                onClick={async()=>{
                  if(!formAuth.nmVisitante.trim()||!formAuth.dtValidade){
                    notify("Nome do visitante e validade são obrigatórios.", "warning");
                    return;
                  }
                  setSaving(true);
                  try{
                    await portariaService.criarAutorizacao({...formAuth,
                      idUnidade:condoAtivo?.idUnidade,idCondominio:condoAtivo?.idCondominio,
                      nmAutorizadoPor:usuario?.nome||""});
                    setShowNovaAuth(false);
                    const r=await portariaService.preautorizacoes({idCondominio:condoAtivo?.idCondominio});
                    setAutorizacoes(r.data||[]);
                    notify("Autorização criada com sucesso!", "success");
                  }catch(e){ notify(e.message || "Erro ao criar autorização.", "danger"); }
                  setSaving(false);
                }}>
                <i className="bi bi-qr-code me-1"/>Criar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL: MORADOR ═══ */}
      {showNovoMor&&(
        <div className="pcp-modal-overlay" onClick={()=>setShowNovoMor(false)}>
          <div className="pcp-modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-person-plus me-2"/>
                {editMorador?"Editar":"Novo"} Morador
              </h5>
              <button className="btn-close" onClick={()=>setShowNovoMor(false)}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-8">
                  <label className="form-label">Nome *</label>
                  <input className="form-control" value={formMor.nmMorador} autoFocus
                    onChange={e=>setFormMor(p=>({...p,nmMorador:e.target.value}))}/>
                </div>
                <div className="col-4">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={formMor.tpMorador}
                    onChange={e=>setFormMor(p=>({...p,tpMorador:e.target.value}))}>
                    {["Familiar","Funcionario","Proprietario"].map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label">Parentesco</label>
                  <input className="form-control" value={formMor.parentesco}
                    onChange={e=>setFormMor(p=>({...p,parentesco:e.target.value}))}
                    placeholder="Filho, Cônjuge..."/>
                </div>
                <div className="col-6">
                  <label className="form-label">Telefone</label>
                  <input className="form-control" value={formMor.telefone}
                    onChange={e=>setFormMor(p=>({...p,telefone:e.target.value}))}/>
                </div>
                <div className="col-6">
                  <label className="form-label">CPF</label>
                  <input className="form-control" value={formMor.cpf}
                    onChange={e=>setFormMor(p=>({...p,cpf:e.target.value}))}/>
                </div>
                <div className="col-12">
                  <FotoPicker foto={formMor.foto||null} label="Foto do Morador"
                    onChange={foto=>setFormMor(p=>({...p,foto}))}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowNovoMor(false)}>Cancelar</button>
              <button className="btn btn-primary" disabled={saving}
                onClick={async()=>{
                  if(!formMor.nmMorador.trim()){ notify("Informe o nome.", "warning"); return; }
                  setSaving(true);
                  try{
                    if(editMorador?.id)
                      await portariaService.atualizarMorador(editMorador.id,{...formMor,
                        idCondominio:condoAtivo?.idCondominio,idUnidade:condoAtivo?.idUnidade});
                    else
                      await portariaService.criarMorador({...formMor,
                        idCondominio:condoAtivo?.idCondominio,idUnidade:condoAtivo?.idUnidade});
                    setShowNovoMor(false);
                    const r=await portariaService.moradores({idCondominio:condoAtivo?.idCondominio,idUnidade:condoAtivo?.idUnidade});
                    setMoradores(r.data||[]);
                    notify(editMorador?.id ? "Morador atualizado!" : "Morador cadastrado!", "success");
                  }catch(e){ notify(e.message || "Erro ao salvar morador.", "danger"); }
                  setSaving(false);
                }}>
                {saving?"Salvando...":"Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL: VOTAÇÃO ═══ */}

      {/* ═══ MODAL: RESULTADOS ═══ */}
      {/* ── Modal Ver Ata ── */}
      {showAta && ataConteudo && (
        <div className="pcp-modal-overlay" onClick={()=>setShowAta(false)}>
          <div className="pcp-modal-box" style={{maxWidth:640}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-file-text me-2"/>Ata da Assembleia</h5>
              <button className="btn-close" onClick={()=>setShowAta(false)}/>
            </div>
            <div className="modal-body" style={{padding:"16px 20px"}}>
              <div style={{background:"var(--bg-surface-alt)",borderRadius:8,padding:"10px 14px",marginBottom:14}}>
                <div style={{fontWeight:700,fontSize:14,color:"#1B3A6B"}}>{ataConteudo.titulo}</div>
                <div style={{fontSize:12,color:"var(--gray-500)",marginTop:2}}>{fmtD(ataConteudo.dtAssembleia)}</div>
              </div>
              {ataConteudo.ataHtml ? (
                <div style={{fontSize:13,lineHeight:1.7,color:"#374151",maxHeight:400,overflowY:"auto",
                  padding:"0 4px"}}
                  dangerouslySetInnerHTML={{__html:ataConteudo.ataHtml}}/>
              ) : (
                <p style={{fontSize:13,color:"var(--gray-400)",textAlign:"center",padding:20}}>
                  <i className="bi bi-file-earmark-x" style={{fontSize:32,display:"block",marginBottom:8}}/>
                  Ata ainda não disponível
                </p>
              )}
              {/* Anexos de Ata */}
              {(ataConteudo.anexos||[]).filter(x=>x.tpAnexo==="Ata").length > 0 && (
                <div style={{marginTop:14,borderTop:"1px solid var(--border-color)",paddingTop:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:"var(--gray-400)",marginBottom:8}}>DOCUMENTOS ANEXADOS</div>
                  {ataConteudo.anexos.filter(x=>x.tpAnexo==="Ata").map((a,i)=>(
                    <button key={i} className="btn btn-outline-secondary btn-sm me-2 mb-2"
                      onClick={()=>{
                        const b64=a.base64?.startsWith("data:")?a.base64:`data:${a.tpArquivo||"application/octet-stream"};base64,${a.base64}`;
                        const lk=document.createElement("a"); lk.href=b64; lk.download=a.nmArquivo||"ata";
                        document.body.appendChild(lk); lk.click(); document.body.removeChild(lk);
                      }}>
                      <i className="bi bi-download me-1"/>{a.nmArquivo}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={()=>setShowAta(false)}>Fechar</button>
              {typeof window !== "undefined" && (
                <button className="btn btn-primary btn-sm" onClick={()=>window.print()}>
                  <i className="bi bi-printer me-1"/>Imprimir
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showResultados && (
        <div className="pcp-modal-overlay" onClick={()=>setShowResultados(false)}>
          <div className="pcp-modal-box" style={{maxWidth:560}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-bar-chart me-2"/>Resultados da Assembleia</h5>
              <button className="btn-close" onClick={()=>setShowResultados(false)}/>
            </div>
            <div className="modal-body">
              {loadingRes ? (
                <div style={{textAlign:"center",padding:20}}>
                  <span className="spinner-border text-primary"/>
                </div>
              ) : resultados && (
                <div>
                  {/* Assembleia info */}
                  <div style={{background:"var(--bg-surface-alt)",borderRadius:8,padding:"10px 14px",marginBottom:14}}>
                    <div style={{fontWeight:700,fontSize:14,color:"#1B3A6B"}}>
                      {resultados.assembleia?.titulo}
                    </div>
                    <div style={{fontSize:12,color:"var(--gray-500)",marginTop:2}}>
                      {fmtD(resultados.assembleia?.dtAssembleia)}
                    </div>
                  </div>

                  {/* Quórum */}
                  {resultados.quorum && (
                    <div style={{background:"var(--bg-highlight)",borderRadius:8,padding:"10px 14px",marginBottom:14,
                      display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div>
                        <div style={{fontWeight:700,color:"#1D4ED8",fontSize:13}}>Quórum</div>
                        <div style={{fontSize:12,color:"#1D4ED8"}}>
                          {resultados.quorum.totalPresencas} de {resultados.quorum.totalUnidades} unidades presentes
                        </div>
                      </div>
                      <div style={{fontSize:28,fontWeight:700,color:"#1D4ED8"}}>
                        {resultados.quorum.pct}%
                      </div>
                    </div>
                  )}

                  {/* Pautas com resultados */}
                  {resultados.pautas?.map((p,i)=>{
                    const total = parseInt(p.totalVotos)||0;
                    const apr  = parseInt(p.qtdAprovado)||0;
                    const rep  = parseInt(p.qtdReprovado)||0;
                    const abs  = parseInt(p.qtdAbstencao)||0;
                    const pctA = total>0?(apr/total*100).toFixed(0):0;
                    const pctR = total>0?(rep/total*100).toFixed(0):0;
                    return (
                      <div key={p.id} style={{borderRadius:8,padding:14,marginBottom:10,
                        border:"1px solid var(--border-color)",background:"var(--bg-surface)"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                          <div style={{fontWeight:600,fontSize:13,flex:1}}>
                            <span style={{background:"var(--bg-highlight)",color:"#1D4ED8",
                              padding:"1px 6px",borderRadius:4,fontSize:10,marginRight:6}}>{i+1}</span>
                            {p.descricao}
                          </div>
                          {p.resultado && (
                            <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:8,
                              background:p.resultado==="Aprovada"?"#DCFCE7":p.resultado==="Reprovada"?"#FEE2E2":"#F1F5F9",
                              color:p.resultado==="Aprovada"?"#166534":p.resultado==="Reprovada"?"#991B1B":"#64748B"}}>
                              {p.resultado}
                            </span>
                          )}
                        </div>
                        {p.tpPauta!=="Informativo" && total > 0 && (
                          <div style={{marginTop:10}}>
                            <div style={{display:"flex",gap:6,marginBottom:6}}>
                              {[["Aprovado",apr,pctA,"#166534"],["Reprovado",rep,pctR,"#991B1B"],["Abstenção",abs,total>0?(abs/total*100).toFixed(0):0,"#854D0E"]].map(([l,n,pct,col])=>(
                                <div key={l} style={{flex:1,textAlign:"center",background:"var(--bg-surface-alt)",
                                  borderRadius:6,padding:"6px 4px"}}>
                                  <div style={{fontSize:18,fontWeight:700,color:col}}>{n}</div>
                                  <div style={{fontSize:10,color:col,fontWeight:600}}>{l}</div>
                                  <div style={{fontSize:10,color:"var(--gray-400)"}}>{pct}%</div>
                                </div>
                              ))}
                            </div>
                            {/* Barra de progresso */}
                            <div style={{height:8,borderRadius:4,overflow:"hidden",
                              background:"var(--bg-page)",display:"flex"}}>
                              <div style={{width:`${pctA}%`,background:"#166534",transition:"width .5s"}}/>
                              <div style={{width:`${pctR}%`,background:"#991B1B",transition:"width .5s"}}/>
                              <div style={{flex:1,background:"#D97706"}}/>
                            </div>
                          </div>
                        )}
                        {p.tpPauta==="Informativo" && (
                          <div style={{fontSize:11,color:"var(--gray-500)",marginTop:6,fontStyle:"italic"}}>Pauta informativa</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowResultados(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {showVotar&&asmAtiva&&(
        <div className="pcp-modal-overlay" onClick={()=>setShowVotar(false)}>
          <div className="pcp-modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-check2-square me-2"/>Votação</h5>
              <button className="btn-close" onClick={()=>setShowVotar(false)}/>
            </div>
            <div className="modal-body" style={{padding:"12px 16px"}}>
              {/* Detalhes da assembleia */}
              <div style={{background:"var(--bg-surface-alt)",borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:12}}>
                <div style={{fontWeight:700,fontSize:14,color:"#1B3A6B",marginBottom:4}}>
                  {asmAtiva.titulo||asmAtiva.tpAssembleia}
                </div>
                <div style={{color:"var(--gray-500)"}}>
                  {asmAtiva.dtAssembleia && (
                    <span><i className="bi bi-calendar3 me-1"/>
                      {fmtD(asmAtiva.dtAssembleia)}
                    </span>
                  )}
                  {asmAtiva.hrInicio && <span className="ms-2"><i className="bi bi-clock me-1"/>{asmAtiva.hrInicio}</span>}
                  {asmAtiva.local && <span className="ms-2"><i className="bi bi-geo-alt me-1"/>{asmAtiva.local}</span>}
                </div>
                {activeCondo && (
                  <div style={{marginTop:4,color:"#1D4ED8",fontWeight:600}}>
                    <i className="bi bi-house me-1"/>Votando pela Unidade {activeCondo.nmUnidade}
                  </div>
                )}
              </div>

              {/* Progress: pautas voted */}
              {!loadingPautas && pautasAsm.length > 0 && (() => {
                const votaveis = pautasAsm.filter(p=>p.tpPauta!=="Informativo");
                const jaVotei  = votaveis.filter(p=>p.votos?.[activeCondo?.idUnidade]).length;
                const pct = votaveis.length>0 ? Math.round(jaVotei/votaveis.length*100) : 0;
                return votaveis.length > 0 && (
                  <div style={{marginBottom:12}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"var(--gray-500)",marginBottom:4}}>
                      <span>Pautas votadas</span>
                      <span style={{fontWeight:700,color:jaVotei===votaveis.length?"#166534":"#1D4ED8"}}>
                        {jaVotei} de {votaveis.length}
                      </span>
                    </div>
                    <div style={{height:6,borderRadius:3,background:"var(--bg-page)"}}>
                      <div style={{height:"100%",borderRadius:3,background:jaVotei===votaveis.length?"#166534":"#1D4ED8",
                        width:`${pct}%`,transition:"width .5s"}}/>
                    </div>
                  </div>
                );
              })()}
              {loadingPautas ? (
                <div style={{textAlign:"center",padding:20}}>
                  <span className="spinner-border text-primary"/>
                </div>
              ) : pautasAsm.length===0 ? (
                <div style={{color:"var(--gray-400)",textAlign:"center",padding:20}}>Nenhuma pauta cadastrada</div>
              ) : (
                <div>
                  {pautasAsm.map((p,idx)=>{
                    const jaVotei = activeCondo?.idUnidade && p.votos?.[activeCondo.idUnidade];
                    const isInfo  = p.tpPauta==="Informativo";
                    return (
                      <div key={p.id} style={{background:jaVotei?"#F0FDF4":"#F8FAFC",borderRadius:8,
                        padding:14,marginBottom:10,border:`1px solid ${jaVotei?"#BBF7D0":"#E2E8F0"}`}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                          <div style={{fontWeight:600,fontSize:13,color:"var(--text-primary)",flex:1}}>
                            <span style={{background:"var(--bg-highlight)",color:"#1D4ED8",padding:"1px 6px",
                              borderRadius:4,fontSize:10,fontWeight:700,marginRight:6}}>
                              {idx+1}
                            </span>
                            {p.descricao}
                          </div>
                          {jaVotei && (
                            <span style={{fontSize:10,fontWeight:700,color:"#166534",
                              background:"var(--bg-success-soft)",padding:"2px 8px",borderRadius:8,flexShrink:0}}>
                              ✓ Votado: {jaVotei==="Aprovado"?"Aprovei":jaVotei==="Reprovado"?"Reprovei":"Abstive"}
                            </span>
                          )}
                        </div>
                        {isInfo ? (
                          <div style={{fontSize:11,color:"var(--gray-500)",marginTop:6,fontStyle:"italic"}}>
                            <i className="bi bi-info-circle me-1"/>Pauta informativa — não requer voto
                          </div>
                        ) : jaVotei ? (
                          <div style={{fontSize:11,color:"#166534",marginTop:6}}>
                            Seu voto foi registrado. Não é possível alterar.
                          </div>
                        ) : (
                          <div style={{display:"flex",gap:6,marginTop:10}}>
                            {[["Aprovado","#166534"],["Reprovado","#991B1B"],["Abstencao","#854D0E"]].map(([v,cor])=>(
                              <button key={v} onClick={()=>setVotos(vs=>({...vs,[p.id]:v}))}
                                style={{flex:1,padding:"8px 4px",borderRadius:6,cursor:"pointer",
                                  fontWeight:600,fontSize:11,transition:"all .15s",
                                  background:votos[p.id]===v?cor:"#fff",
                                  color:votos[p.id]===v?"#fff":cor,
                                  border:`2px solid ${cor}`}}>
                                {v==="Aprovado"?"✓ Aprovar":v==="Reprovado"?"✗ Reprovar":"— Abster"}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="modal-footer">
              {(() => {
                const idU = activeCondo?.idUnidade;
                const jaVotouNaPauta = (p) =>
                  idU != null && (p.votos?.[idU] ?? p.votos?.[String(idU)]) != null;
                const temSelecaoParaEnviar = pautasAsm.some(
                  (p) => p.tpPauta !== "Informativo" && !jaVotouNaPauta(p) && !!votos[p.id],
                );
                return (
                  <>
                    <button className="btn btn-outline-secondary" onClick={()=>setShowVotar(false)}>Cancelar</button>
                    <button
                      className="btn btn-primary"
                      disabled={salvandoVoto || !temSelecaoParaEnviar}
                      onClick={handleVotar}
                    >
                      {salvandoVoto ? "Enviando..." : "Confirmar Votos"}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL: NOVO VEÍCULO ═══ */}
      {showNovoVei && (
        <div className="pcp-modal-overlay" onClick={()=>setShowNovoVei(false)}>
          <div className="pcp-modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-car-front me-2"/>{editVeiculo?"Editar":"Novo"} Veículo</h5>
              <button className="btn-close" onClick={()=>setShowNovoVei(false)}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-7">
                  <label className="form-label">Placa *</label>
                  <input className="form-control" style={{textTransform:"uppercase",fontFamily:"monospace",fontSize:16,letterSpacing:2}}
                    value={formVei.placa} autoFocus
                    onChange={e=>setFormVei(p=>({...p,placa:e.target.value.toUpperCase()}))}
                    placeholder="ABC-1234"/>
                </div>
                <div className="col-5">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={formVei.tpVeiculo}
                    onChange={e=>setFormVei(p=>({...p,tpVeiculo:e.target.value}))}>
                    {["Carro","Moto","Caminhonete","Van","Outro"].map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-8">
                  <label className="form-label">Modelo</label>
                  <input className="form-control" value={formVei.modelo||""} placeholder="Honda Civic"
                    onChange={e=>setFormVei(p=>({...p,modelo:e.target.value}))}/>
                </div>
                <div className="col-4">
                  <label className="form-label">Cor</label>
                  <input className="form-control" value={formVei.cor||""} placeholder="Prata"
                    onChange={e=>setFormVei(p=>({...p,cor:e.target.value}))}/>
                </div>
                <div className="col-12">
                  <FotoPicker foto={formVei.foto} label="Foto do Veículo"
                    onChange={foto=>setFormVei(p=>({...p,foto}))}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowNovoVei(false)}>Cancelar</button>
              <button className="btn btn-primary" disabled={savingVei}
                onClick={async()=>{
                  if(!formVei.placa.trim()){ notify("Informe a placa.", "warning"); return; }
                  setSavingVei(true);
                  try {
                    const u=activeCondo;
                    const payload={...formVei,idUnidade:u?.idUnidade};
                    if(editVeiculo?.id) await portalService.atualizarVeiculo(editVeiculo.id,payload);
                    else               await portalService.criarVeiculo(payload);
                    setShowNovoVei(false);
                    const r=await portalService.veiculos(activeCondo?.idCondominio, activeCondo?.idUnidade); setVeiculos(r.data||[]);
                    notify(editVeiculo?.id ? "Veículo atualizado!" : "Veículo cadastrado!", "success");
                  } catch(e){ notify(e.message || "Erro ao salvar veículo.", "danger"); }
                  setSavingVei(false);
                }}>
                {savingVei?"Salvando...":"Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL: NOVO PET ═══ */}
      {showNovoPet && (
        <div className="pcp-modal-overlay" onClick={()=>setShowNovoPet(false)}>
          <div className="pcp-modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">🐾 {editPet?"Editar":"Novo"} Pet</h5>
              <button className="btn-close" onClick={()=>setShowNovoPet(false)}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-7">
                  <label className="form-label">Nome *</label>
                  <input className="form-control" value={formPet.nmPet} autoFocus
                    onChange={e=>setFormPet(p=>({...p,nmPet:e.target.value}))}
                    placeholder="Nome do pet"/>
                </div>
                <div className="col-5">
                  <label className="form-label">Espécie</label>
                  <select className="form-select" value={formPet.especie}
                    onChange={e=>setFormPet(p=>({...p,especie:e.target.value}))}>
                    {["Cachorro","Gato","Pássaro","Peixe","Réptil","Outro"].map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label">Raça</label>
                  <input className="form-control" value={formPet.raca||""} placeholder="Ex: Labrador"
                    onChange={e=>setFormPet(p=>({...p,raca:e.target.value}))}/>
                </div>
                <div className="col-6">
                  <label className="form-label">Cor / Pelagem</label>
                  <input className="form-control" value={formPet.cor||""}
                    onChange={e=>setFormPet(p=>({...p,cor:e.target.value}))}/>
                </div>
                <div className="col-12">
                  <label className="form-label">Observação</label>
                  <input className="form-control" value={formPet.observacao||""} placeholder="Detalhes adicionais"
                    onChange={e=>setFormPet(p=>({...p,observacao:e.target.value}))}/>
                </div>
                <div className="col-12">
                  <FotoPicker foto={formPet.foto} label="Foto do Pet"
                    onChange={foto=>setFormPet(p=>({...p,foto}))}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowNovoPet(false)}>Cancelar</button>
              <button className="btn btn-primary" disabled={savingPet}
                onClick={async()=>{
                  if(!formPet.nmPet.trim()){ notify("Informe o nome do pet.", "warning"); return; }
                  setSavingPet(true);
                  try {
                    const u=activeCondo;
                    const payload={...formPet,idUnidade:u?.idUnidade};
                    if(editPet?.id) await portalService.atualizarPet(editPet.id,payload);
                    else            await portalService.criarPet(payload);
                    setShowNovoPet(false);
                    const r=await portalService.pets(activeCondo?.idCondominio, activeCondo?.idUnidade); setPets(r.data||[]);
                    notify(editPet?.id ? "Pet atualizado!" : "Pet cadastrado!", "success");
                  } catch(e){ notify(e.message || "Erro ao salvar pet.", "danger"); }
                  setSavingPet(false);
                }}>
                {savingPet?"Salvando...":"Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* ── Modal detalhe do boleto ── */}
      {boletoDetalhe && (
        <div style={{
          position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:99999,
          background:"rgba(15,23,42,.75)",backdropFilter:"blur(6px)",
          display:"flex",alignItems:"flex-end",justifyContent:"center",
          isolation:"isolate",willChange:"transform",
        }} onClick={()=>setBoletoDetalhe(null)}>
          <div onClick={e=>e.stopPropagation()} style={{
            background:"var(--bg-surface)",width:"100%",maxWidth:520,
            borderRadius:"20px 20px 0 0",
            padding:"8px 0 0",
            maxHeight:"92vh",overflowY:"auto",
          }}>
            <div style={{textAlign:"center",paddingBottom:12}}>
              <div style={{width:40,height:4,background:"#E2E8F0",borderRadius:2,display:"inline-block"}}/>
            </div>
            <div style={{padding:"0 20px 16px",borderBottom:"1px solid var(--border-subtle)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div>
                  <div style={{fontWeight:800,fontSize:16,color:"#1B3A6B"}}>
                    {boletoDetalhe.nmFantasia||boletoDetalhe.nmCondominio}
                  </div>
                  <div style={{fontSize:12,color:"var(--gray-400)",marginTop:2}}>
                    {boletoDetalhe.competencia||""}{boletoDetalhe.unidade?` · Apto ${boletoDetalhe.unidade}`:""}
                  </div>
                </div>
                <button onClick={()=>setBoletoDetalhe(null)} style={{
                  background:"var(--bg-page)",border:"none",borderRadius:"50%",
                  width:32,height:32,cursor:"pointer",fontSize:18,
                  display:"flex",alignItems:"center",justifyContent:"center",
                }}>×</button>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:12,marginTop:14}}>
                <div style={{fontSize:32,fontWeight:900,letterSpacing:"-1px",
                  color:STC[boletoDetalhe.statusBoleto]||"#1E293B"}}>
                  {fmtVal(boletoDetalhe.vlrBoleto)}
                </div>
                <span style={{fontSize:12,fontWeight:700,padding:"4px 10px",borderRadius:20,
                  background:STB[boletoDetalhe.statusBoleto]||"#F1F5F9",
                  color:STC[boletoDetalhe.statusBoleto]||"#64748B"}}>
                  {boletoDetalhe.statusBoleto}
                </span>
              </div>
              <div style={{fontSize:12,color:"var(--gray-400)",marginTop:4}}>
                Vencimento: <strong>{fmtD(boletoDetalhe.dtVencimento)}</strong>
              </div>
            </div>
            {boletoDetalhe.linhaDigitavel && (
              <div style={{padding:"16px 20px",borderBottom:"1px solid var(--border-subtle)"}}>
                <div style={{fontSize:11,fontWeight:700,color:"var(--gray-500)",marginBottom:8,textTransform:"uppercase",letterSpacing:".5px"}}>
                  Linha Digitável
                </div>
                <div style={{background:"var(--bg-surface-alt)",borderRadius:10,padding:"12px 14px",
                  fontFamily:"monospace",fontSize:13,letterSpacing:".5px",
                  color:"var(--text-primary)",wordBreak:"break-all",marginBottom:10}}>
                  {boletoDetalhe.linhaDigitavel}
                </div>
                <button onClick={()=>handleCopiarLinha(boletoDetalhe.linhaDigitavel)}
                  style={{width:"100%",padding:"12px",
                    background:copiado?"#10B981":"#1B3A6B",
                    color:"#fff",border:"none",borderRadius:10,
                    fontWeight:700,fontSize:14,cursor:"pointer",
                    transition:"background .2s"}}>
                  {copiado?<><i className="bi bi-check-lg me-2"/>Copiado!</>:<><i className="bi bi-clipboard me-2"/>Copiar Linha Digitável</>}
                </button>
              </div>
            )}
            {boletoDetalhe.qrCodePix && boletoDetalhe.statusBoleto !== "Pago" && (
              <div style={{padding:"16px 20px",borderBottom:"1px solid var(--border-subtle)"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#166534",marginBottom:12,textTransform:"uppercase",letterSpacing:".5px"}}>
                  💚 Pague via PIX
                </div>
                <div style={{display:"flex",gap:16,alignItems:"center"}}>
                  <div style={{background:"var(--bg-surface)",padding:10,borderRadius:12,border:"2px solid #86EFAC",flexShrink:0}}>
                    <img src={boletoDetalhe.qrCodePix} alt="QR Code PIX" style={{width:140,height:140,display:"block"}}/>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,color:"#166534",fontWeight:600,marginBottom:6}}>
                      Escaneie com o app do seu banco
                    </div>
                    {boletoDetalhe.chavePix&&(
                      <div style={{background:"var(--bg-success-soft)",padding:"6px 8px",borderRadius:6,
                        fontSize:11,fontFamily:"monospace",wordBreak:"break-all",color:"#166534"}}>
                        {boletoDetalhe.chavePix}
                      </div>
                    )}
                    <div style={{fontSize:10,color:"var(--gray-400)",marginTop:8}}>
                      ⚠️ Valor fixo: <strong style={{color:"#166534"}}>{fmtVal(boletoDetalhe.vlrBoleto)}</strong>
                    </div>
                  </div>
                </div>

                {/* PIX Copia e Cola */}
                {boletoDetalhe.pixPayload && (
                  <div style={{marginTop:14}}>
                    <button
                      onClick={()=>handleCopiarPix(boletoDetalhe.pixPayload)}
                      style={{width:"100%",padding:"12px",
                        background: pixCopiado ? "#10B981" : "#166534",
                        color:"#fff",border:"none",borderRadius:10,
                        fontWeight:700,fontSize:14,cursor:"pointer",
                        transition:"background .2s",
                        display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                      {pixCopiado ? <><i className="bi bi-check-lg"/>PIX Copiado!</>
                                  : <><i className="bi bi-clipboard"/>Copiar código PIX</>}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Boleto Pago — feedback positivo */}
            {boletoDetalhe.statusBoleto === "Pago" && (
              <div style={{padding:"20px",borderBottom:"1px solid var(--border-subtle)",textAlign:"center"}}>
                <i className="bi bi-check-circle-fill" style={{fontSize:48,color:"#10B981"}}/>
                <div style={{fontSize:14,fontWeight:700,color:"#065F46",marginTop:8}}>
                  Pagamento confirmado!
                </div>
                {boletoDetalhe.dtPagamento && (
                  <div style={{fontSize:12,color:"var(--gray-500)",marginTop:2}}>
                    em {fmtD(boletoDetalhe.dtPagamento)}
                  </div>
                )}
              </div>
            )}

            <div style={{padding:"16px 20px 24px",display:"flex",gap:10,flexWrap:"wrap"}}>
              <button onClick={()=>handleImprimirBoleto(boletoDetalhe.id)}
                style={{flex:"1 1 140px",padding:"13px",background:"var(--bg-surface-alt)",color:"#1B3A6B",
                  border:"2px solid #1B3A6B",borderRadius:12,
                  fontWeight:700,fontSize:14,cursor:"pointer",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                <i className="bi bi-printer"/>Imprimir
              </button>
              {boletoDetalhe.tokenPublico && (
                <button onClick={()=>handleCompartilharBoleto(boletoDetalhe)}
                  style={{flex:"1 1 140px",padding:"13px",background:"#25D366",color:"#fff",
                    border:"none",borderRadius:12,
                    fontWeight:700,fontSize:14,cursor:"pointer",
                    display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  <i className="bi bi-whatsapp"/>Compartilhar
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Meu Perfil ────────────────────────────────────────────── */}
      {showPerfilModal && (
        <div className="pcp-modal-overlay" onClick={()=>setShowPerfilModal(false)}>
          <div className="pcp-modal-box" onClick={e=>e.stopPropagation()} style={{maxWidth:540}}>
            {/* Header */}
            <div style={{padding:"16px 20px",borderBottom:"1px solid var(--border-color)",
              display:"flex",alignItems:"center",justifyContent:"space-between",
              background:"linear-gradient(135deg,#1B3A6B,#2563EB)",borderRadius:"14px 14px 0 0"}}>
              <div style={{display:"flex",alignItems:"center",gap:10,color:"#fff"}}>
                <i className="bi bi-person-circle" style={{fontSize:22}}/>
                <div>
                  <div style={{fontSize:15,fontWeight:700}}>Meu Perfil</div>
                  <div style={{fontSize:11,opacity:.85}}>{perfilData?.usuario?.nome || usuario?.nome}</div>
                </div>
              </div>
              <button onClick={()=>setShowPerfilModal(false)}
                style={{background:"none",border:"none",color:"#fff",fontSize:20,cursor:"pointer",lineHeight:1}}>
                <i className="bi bi-x-lg"/>
              </button>
            </div>

            {/* Tabs */}
            <div style={{display:"flex",borderBottom:"1px solid var(--border-color)",background:"var(--bg-surface-alt)"}}>
              {[
                { id:"foto",         icon:"bi-camera",         label:"Foto" },
                { id:"senha",        icon:"bi-key",            label:"Senha" },
                { id:"contato",      icon:"bi-envelope-at",    label:"Contato" },
                { id:"notificacoes", icon:"bi-bell",           label:"Notif." },
                { id:"sessoes",      icon:"bi-shield-check",   label:"Sessões" },
                { id:"preferencias", icon:"bi-gear",           label:"Pref." },
              ].map(t=>(
                <button key={t.id}
                  onClick={()=>{ setPerfilTab(t.id); setPerfilMsg(null); }}
                  style={{flex:1,padding:"12px 8px",border:"none",
                    background:perfilTab===t.id?"#fff":"transparent",
                    color:perfilTab===t.id?"#1B3A6B":"#64748B",
                    fontWeight:perfilTab===t.id?700:500,fontSize:13,
                    borderBottom:`3px solid ${perfilTab===t.id?"#1B3A6B":"transparent"}`,
                    cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                  <i className={`bi ${t.icon}`}/>{t.label}
                </button>
              ))}
            </div>

            {/* Banner de feedback */}
            {perfilMsg && (
              <div style={{margin:"12px 16px 0",padding:"10px 14px",borderRadius:8,
                fontSize:13,fontWeight:500,
                background:
                  perfilMsg.type==="success"?"#DCFCE7":
                  perfilMsg.type==="danger" ?"#FEE2E2":
                  perfilMsg.type==="warning"?"#FEF3C7":"#E0F2FE",
                color:
                  perfilMsg.type==="success"?"#166534":
                  perfilMsg.type==="danger" ?"#991B1B":
                  perfilMsg.type==="warning"?"#92400E":"#075985",
                border:`1px solid ${
                  perfilMsg.type==="success"?"#86EFAC":
                  perfilMsg.type==="danger" ?"#FCA5A5":
                  perfilMsg.type==="warning"?"#FDE68A":"#7DD3FC"}`}}>
                <i className={`bi ${perfilMsg.type==="success"?"bi-check-circle":perfilMsg.type==="danger"?"bi-x-circle":"bi-info-circle"} me-2`}/>
                {perfilMsg.text}
              </div>
            )}

            {/* Body */}
            <div style={{padding:"20px"}}>
              {/* TAB FOTO */}
              {perfilTab==="foto" && (
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:14}}>
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:14,fontWeight:700,color:"var(--text-primary)"}}>
                      {perfilData?.condomino?.nmCompleto || perfilData?.usuario?.nome || "—"}
                    </div>
                    <div style={{fontSize:12,color:"var(--gray-500)",marginTop:2}}>
                      {perfilData?.usuario?.email}
                    </div>
                  </div>

                  <AvatarPicker
                    valor={perfilData?.condomino?.avatar}
                    nome={perfilData?.condomino?.nmCompleto || perfilData?.usuario?.nome || ""}
                    loading={perfilSaving}
                    onSave={(b64) => salvarAvatar(b64)}
                    onRemove={() => setConfirmRemoverAvatar(true)}
                    onErro={(msg) => setPerfilMsg({ type: "warning", text: msg })}
                  />
                </div>
              )}

              {/* TAB SENHA */}
              {perfilTab==="senha" && (
                <div style={{display:"flex",flexDirection:"column",gap:14}}>
                  <div style={{fontSize:13,color:"var(--gray-500)"}}>
                    Para sua segurança, informe a senha atual e a nova senha (mínimo 6 caracteres).
                  </div>

                  <div>
                    <label style={{fontSize:12,fontWeight:600,marginBottom:4,display:"block",color:"var(--gray-600)"}}>
                      Senha atual
                    </label>
                    <input type={showSenhas?"text":"password"} className="form-control"
                      value={senhaForm.atual} disabled={perfilSaving} autoComplete="current-password"
                      onChange={e=>setSenhaForm(s=>({...s,atual:e.target.value}))}
                      placeholder="Digite sua senha atual"/>
                  </div>
                  <div>
                    <label style={{fontSize:12,fontWeight:600,marginBottom:4,display:"block",color:"var(--gray-600)"}}>
                      Nova senha
                    </label>
                    <input type={showSenhas?"text":"password"} className="form-control"
                      value={senhaForm.nova} disabled={perfilSaving} autoComplete="new-password"
                      onChange={e=>setSenhaForm(s=>({...s,nova:e.target.value}))}
                      placeholder="Mínimo 6 caracteres"/>
                    <PasswordStrengthBar senha={senhaForm.nova} />
                  </div>
                  <div>
                    <label style={{fontSize:12,fontWeight:600,marginBottom:4,display:"block",color:"var(--gray-600)"}}>
                      Confirmar nova senha
                    </label>
                    <input type={showSenhas?"text":"password"} className="form-control"
                      value={senhaForm.confirma} disabled={perfilSaving} autoComplete="new-password"
                      onChange={e=>setSenhaForm(s=>({...s,confirma:e.target.value}))}
                      placeholder="Repita a nova senha"/>
                    {senhaForm.confirma && senhaForm.nova !== senhaForm.confirma && (
                      <div style={{fontSize:11,color:"#991B1B",marginTop:4}}>
                        <i className="bi bi-exclamation-circle me-1"/>
                        As senhas não conferem.
                      </div>
                    )}
                  </div>

                  <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"var(--gray-500)",cursor:"pointer"}}>
                    <input type="checkbox" checked={showSenhas}
                      onChange={e=>setShowSenhas(e.target.checked)}/>
                    Mostrar senhas
                  </label>

                  <button className="btn btn-primary" disabled={perfilSaving}
                    onClick={salvarSenha}>
                    {perfilSaving ? <><span className="spinner-border spinner-border-sm me-2"/>Salvando...</>
                                   : <><i className="bi bi-shield-lock me-2"/>Alterar senha</>}
                  </button>

                  <div style={{borderTop:"1px solid var(--border-color)",paddingTop:14,marginTop:4}}>
                    <div style={{fontSize:12,fontWeight:600,color:"var(--gray-600)",marginBottom:6}}>
                      <i className="bi bi-shield-exclamation me-1"/>
                      Sessões em outros dispositivos
                    </div>
                    <div style={{fontSize:11,color:"var(--gray-400)",marginBottom:8,lineHeight:1.4}}>
                      Suspeita que sua conta esteja sendo acessada em outro lugar? Desconecte todas as sessões. Você precisará fazer login novamente.
                    </div>
                    <button type="button" className="btn btn-outline-danger btn-sm"
                      disabled={perfilSaving}
                      onClick={()=>setConfirmLogoutAll(true)}>
                      <i className="bi bi-box-arrow-right me-1"/>
                      Encerrar todas as sessões
                    </button>
                  </div>
                </div>
              )}

              {/* TAB CONTATO */}
              {perfilTab==="contato" && (
                <PortalContatoForm
                  perfilData={perfilData}
                  onUpdated={carregarPerfil}
                />
              )}

              {/* TAB NOTIFICAÇÕES (Web Push) */}
              {perfilTab==="notificacoes" && (
                <PortalNotificacoesForm />
              )}

              {/* TAB SESSÕES ATIVAS */}
              {perfilTab==="sessoes" && (
                <PortalSessoesForm />
              )}

              {/* TAB PREFERÊNCIAS */}
              {perfilTab==="preferencias" && (
                <div style={{display:"flex",flexDirection:"column",gap:14}}>
                  {!perfilData?.condomino ? (
                    <div style={{padding:20,textAlign:"center",color:"var(--gray-400)",fontSize:13}}>
                      <i className="bi bi-info-circle me-2"/>
                      Não foi possível carregar seu cadastro de condômino.
                    </div>
                  ) : (
                    <>
                      <div>
                        <label style={{fontSize:12,fontWeight:600,marginBottom:4,display:"block",color:"var(--gray-600)"}}>
                          Data de nascimento
                        </label>
                        <input type="date" className="form-control"
                          value={perfilData?.condomino?.dtNascimento ? String(perfilData.condomino.dtNascimento).split("T")[0] : ""}
                          disabled={perfilSaving}
                          onChange={e=>setPerfilData(p=>({...p, condomino:{...p.condomino, dtNascimento:e.target.value||null}}))}/>
                        <div style={{fontSize:11,color:"var(--gray-400)",marginTop:4}}>
                          Usada para parabenizar você no seu aniversário (se você optar).
                        </div>
                      </div>

                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                        padding:"12px 14px",background:"var(--bg-surface-alt)",borderRadius:10,
                        border:"1px solid var(--border-color)"}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--text-primary)"}}>
                            <i className="bi bi-gift me-2"/>Receber parabéns de aniversário
                          </div>
                          <div style={{fontSize:11,color:"var(--gray-500)",marginTop:2}}>
                            Mensagem automática por e-mail/WhatsApp no seu dia.
                          </div>
                        </div>
                        <label style={{position:"relative",display:"inline-block",width:44,height:24,cursor:"pointer"}}>
                          <input type="checkbox"
                            checked={!!perfilData?.condomino?.aniversarioOptIn}
                            disabled={perfilSaving}
                            onChange={e=>setPerfilData(p=>({...p, condomino:{...p.condomino, aniversarioOptIn:e.target.checked?1:0}}))}
                            style={{opacity:0,width:0,height:0}}/>
                          <span style={{
                            position:"absolute",inset:0,borderRadius:24,
                            background: perfilData?.condomino?.aniversarioOptIn ? "#1B3A6B" : "#CBD5E1",
                            transition:"background .2s"}}/>
                          <span style={{
                            position:"absolute",top:2,
                            left: perfilData?.condomino?.aniversarioOptIn ? 22 : 2,
                            width:20,height:20,background:"var(--bg-surface)",borderRadius:"50%",
                            transition:"left .2s",boxShadow:"0 2px 4px rgba(0,0,0,.2)"}}/>
                        </label>
                      </div>

                      <button className="btn btn-primary" disabled={perfilSaving}
                        onClick={salvarPreferencias}>
                        {perfilSaving ? <><span className="spinner-border spinner-border-sm me-2"/>Salvando...</>
                                       : <><i className="bi bi-check2-circle me-2"/>Salvar preferências</>}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmação: remover avatar ── */}
      {confirmRemoverAvatar && (
        <ConfirmModal
          titulo="Remover foto de perfil?"
          mensagem="Sua foto será apagada e você ficará com a foto padrão (iniciais). Esta ação pode ser desfeita enviando uma nova foto."
          perigo
          textoOk="Remover"
          carregando={perfilSaving}
          onConfirm={async () => {
            await salvarAvatar(null);
            setConfirmRemoverAvatar(false);
          }}
          onCancel={() => setConfirmRemoverAvatar(false)}
        />
      )}

      {/* ── Confirmação: logout de todas as sessões ── */}
      {confirmLogoutAll && (
        <ConfirmModal
          titulo="Encerrar todas as sessões?"
          mensagem="Você será desconectado de todos os dispositivos (incluindo este). Será necessário fazer login novamente."
          perigo
          textoOk="Sim, encerrar tudo"
          carregando={perfilSaving}
          onConfirm={async () => {
            try {
              setPerfilSaving(true);
              await portalService.logoutTodasSessoes();
              setConfirmLogoutAll(false);
              setShowPerfilModal(false);
              // Aguarda 600ms para o usuário ver a transição e força logout
              setTimeout(() => sair?.(), 600);
            } catch (e) {
              setPerfilMsg({ type:"danger", text: e?.message || "Erro ao encerrar sessões." });
            } finally {
              setPerfilSaving(false);
            }
          }}
          onCancel={() => setConfirmLogoutAll(false)}
        />
      )}

      {/* ── Banner global de notificação ── */}
      {banner && (
        <div style={{
          position:"fixed", top:70, left:0, right:0,
          zIndex:1100, display:"flex", justifyContent:"center",
          pointerEvents:"none", padding:"0 16px"
        }}>
          <div style={{ maxWidth:520, width:"100%", pointerEvents:"auto" }}>
            <Banner tipo={banner.tipo} onClose={() => setBanner(null)}>
              {banner.mensagem}
            </Banner>
          </div>
        </div>
      )}

      {/* ── Modal de confirmação global ── */}
      {confirmacao && (
        <ConfirmModal
          titulo={confirmacao.titulo}
          mensagem={confirmacao.mensagem}
          textoOk={confirmacao.textoOk}
          perigo={confirmacao.perigo}
          onConfirm={() => { confirmacao.resolve(true); setConfirmacao(null); }}
          onCancel={() => { confirmacao.resolve(false); setConfirmacao(null); }}
        />
      )}

    </div>

  );
}
